import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  Colors,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import api from '../services/axiosInstance';
import { backendPort } from '../components/Fungsiku';
import { ShowPic } from '../components/ShowPic';
import { ModalCariMT, ModalCariBan } from './Page27_Cari';
import { ModalBanShow } from './Page28_BanShow';
import { PageKonten34_ShowDataLaporan } from './Page34_ShowDataLaporan';
import { PageKonten9_Pict as LampiranUpload } from './Page9_Pict';
import { useNotification } from './NotifPopup';

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
);

const palette = {
  ink: '#0F172A',
  muted: '#7C8A9A',
  panel: '#FFFFFF',
  bg: '#F3F6FB',
  brand: '#0B3C5D',
  cyan: '#0EA5A4',
  amber: '#D97706',
  red: '#DC2626',
  blue: '#2563EB',
  green: '#059669',
  border: '#D9E2EC',
};

const REQ_GROUP_DASHBOARD_MINICARD = 'dashboard-minicard-v1';
const REQ_GROUP_DASHBOARD_MONITORING_BAN = 'dashboard-monitoring-ban-v1';
const REQ_GROUP_DASHBOARD_PENDING_ALERTS = 'dashboard-pending-alerts-v1';
const REQ_GROUP_DASHBOARD_GRAFIK_TREN = 'dashboard-grafik-tren-v1';
const REQ_GROUP_DASHBOARD_BELANJA = 'dashboard-belanja-v1';
const REQ_GROUP_DASHBOARD_BELANJA_DETAIL = 'dashboard-belanja-detail-v1';
const REQ_GROUP_DASHBOARD_DAFTAR_HADIR = 'dashboard-daftar-hadir-v1';
const REQ_GROUP_DASHBOARD_DAFTAR_HADIR_BULANAN = 'dashboard-daftar-hadir-bulanan-v1';
const REQ_GROUP_DASHBOARD_SUPERVISI_ABSENSI = 'dashboard-supervisi-absensi-v1';
const REQ_GROUP_DASHBOARD_SUPERVISI_ABSENSI_SUBMIT = 'dashboard-supervisi-absensi-submit-v1';
const REQ_GROUP_DASHBOARD_ABSEN_TERJADWAL = 'dashboard-absen-terjadwal-v1';
const REQ_GROUP_DASHBOARD_ABSEN_TERJADWAL_SUBMIT = 'dashboard-absen-terjadwal-submit-v1';
const REQ_GROUP_DASHBOARD_ABSEN_TERJADWAL_DELETE = 'dashboard-absen-terjadwal-delete-v1';
const DASHBOARD_MINICARD_CACHE_NS = 'dashboard_minicard_cache_v1';
const DASHBOARD_MONITORING_PRELOAD_CACHE_NS = 'dashboard_monitoring_preload_cache_v1';
const DASHBOARD_GRAFIK_TREN_CACHE_NS = 'dashboard_grafik_tren_cache_v1';
const DASHBOARD_BELANJA_CACHE_NS = 'dashboard_belanja_cache_v1';
const DASHBOARD_NAV_INTENT_KEY = '__dashboard_nav_intent';
const JAKARTA_TIMEZONE = 'Asia/Jakarta';

const cabangProductivity = {
  labels: ['C-1 Pusat', 'C-2 Surabaya', 'C-3 Makassar', 'C-4 Medan', 'C-5 Semarang'],
  datasets: [
    {
      label: 'Realisasi WO Mingguan',
      data: [216, 172, 151, 143, 118],
      backgroundColor: [
        'rgba(11,60,93,0.92)',
        'rgba(14,165,164,0.88)',
        'rgba(37,99,235,0.86)',
        'rgba(5,150,105,0.86)',
        'rgba(217,119,6,0.86)',
      ],
      borderRadius: 10,
      borderSkipped: false,
      maxBarThickness: 28,
    },
  ],
};

const riskUnits = {
  labels: ['MT-1142', 'MT-0773', 'MT-0899', 'MT-1018', 'MT-0664'],
  datasets: [
    {
      label: 'Hari Terlambat Last Check',
      data: [4, 3, 3, 2, 2],
      backgroundColor: 'rgba(220,38,38,0.82)',
      borderRadius: 10,
      borderSkipped: false,
      maxBarThickness: 24,
    },
  ],
};

const MONITORING_BAN_CONDITIONS = [
  'MT odo belum update >= 2 hari',
  'MT dengan status Non Aktif',
  'Ban Aus Tidak Merata',
  'Pasangan Ban dengan TD Tidak Seimbang',
  'TD Stagnan setelah berjalan > 10.000 KM',
  'Ban dengan TDmin <= 3 mm',
  'Ban di Gudang lebih dari 3 bulan',
  'Ban di pabrik lebih dari 3 bulan',
];
const MONITORING_MT_NONAKTIF_CONDITION = 'MT dengan status Non Aktif';

const SUPERVISI_STATUS_OPTIONS = ['HADIR', 'LIBUR', 'IJIN', 'SAKIT', 'CUTI', 'DINAS LUAR', 'ALPA', 'NON AKTIF'];
const ABSEN_TERJADWAL_STATUS_OPTIONS = ['CUTI', 'IJIN', 'SAKIT', 'LIBUR', 'DINAS LUAR', 'NON AKTIF'];

const chartCardBase = {
  background: palette.panel,
  border: `1px solid ${palette.border}`,
  borderRadius: 18,
  boxShadow: '0 8px 30px rgba(15, 23, 42, 0.06)',
};

const compactGrid = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
};

const baseOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: '#334155',
        font: { family: 'Manrope, Segoe UI, sans-serif', size: 11, weight: 600 },
      },
    },
    tooltip: {
      backgroundColor: '#0F172A',
      titleColor: '#E2E8F0',
      bodyColor: '#E2E8F0',
      padding: 10,
      cornerRadius: 10,
    },
  },
  scales: {
    x: {
      ticks: { color: '#64748B', font: { family: 'Manrope, Segoe UI, sans-serif' } },
      grid: { display: false },
    },
    y: {
      ticks: { color: '#64748B', font: { family: 'Manrope, Segoe UI, sans-serif' } },
      grid: { color: 'rgba(148, 163, 184, 0.20)' },
    },
  },
};

function MiniCard({
  title,
  value,
  delta,
  tone,
  subtitleGridCols = 1,
  subtitleItems = null,
  subtitleRowGap = 4,
  subtitleLineHeight = 1.05,
  subtitlePaddingTop = 10,
}) {
  const toneColor = tone === 'good' ? palette.green : tone === 'risk' ? palette.red : tone === 'neutral' ? palette.blue : palette.amber;
  return (
    <div
      style={{
        ...chartCardBase,
        padding: 14,
        paddingBottom: 18,
        position: 'relative',
        overflow: 'hidden',
        minHeight: 138,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: toneColor,
        }}
      />
      <div style={{ color: palette.muted, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</div>
      <div style={{ marginTop: 6, color: palette.ink, fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{value}</div>
      <div style={{ marginTop: 'auto', paddingTop: subtitlePaddingTop, color: palette.muted, fontSize: 12, fontWeight: 600 }}>
        {Array.isArray(subtitleItems) && subtitleItems.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.max(1, Number(subtitleGridCols || 1))}, minmax(0, 1fr))`,
              columnGap: 4,
              rowGap: Number(subtitleRowGap || 0),
              alignContent: 'start',
              alignItems: 'start',
            }}
          >
            {subtitleItems.map((item, idx) => {
              const action = item && typeof item === 'object' ? item : null;
              const label = action ? String(action.label || '') : String(item || '');
              if (typeof action?.onClick !== 'function') {
                return <div key={`${title}-sub-${idx}`} style={{ whiteSpace: 'normal', lineHeight: subtitleLineHeight, margin: 0, padding: 0 }}>{label}</div>;
              }
              return (
                <button
                  key={`${title}-sub-${idx}`}
                  type="button"
                  onClick={action.onClick}
                  disabled={Boolean(action.disabled)}
                  style={{ appearance: 'none', border: 0, background: 'transparent', padding: 0, color: action.color || 'inherit', font: 'inherit', fontWeight: 'inherit', textAlign: 'left', whiteSpace: 'normal', lineHeight: subtitleLineHeight, textDecoration: 'underline', textDecorationThickness: '1px', textUnderlineOffset: '2px', cursor: action.disabled ? 'not-allowed' : 'pointer', opacity: action.disabled ? 0.55 : 1 }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        ) : delta}
      </div>
    </div>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div className="mb-2">
      <div style={{ color: palette.ink, fontWeight: 800, fontSize: 16 }}>{title}</div>
      <div style={{ color: palette.muted, fontSize: 12 }}>{subtitle}</div>
    </div>
  );
}

export function Dashboard({ setPage, authIdentity = {}, globalOpsi, manualResyncGlobalOpsi }) {
  const { showNotif } = useNotification();
  const [miniSnapshot, setMiniSnapshot] = useState({});
  const [miniContext, setMiniContext] = useState({ last_year: new Date().getFullYear() - 1, day_of_year: 1, days_in_year: 365 });
  const [showMtPicker, setShowMtPicker] = useState(false);
  const [showBanPicker, setShowBanPicker] = useState(false);
  const [showBanDetailModal, setShowBanDetailModal] = useState(false);
  const [selectedBanId, setSelectedBanId] = useState(0);
  const [showLastBanTrxModal, setShowLastBanTrxModal] = useState(false);
  const [lastBanTrxRows, setLastBanTrxRows] = useState([]);
  const [lastBanTrxLoading, setLastBanTrxLoading] = useState(false);
  const [lastBanTrxError, setLastBanTrxError] = useState('');
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [belanjaYtd, setBelanjaYtd] = useState([
    { key: 'Pengadaan Ban', value: 0, color: '#0B3C5D' },
    { key: 'Retread', value: 0, color: '#0EA5A4' },
  ]);
  const [daftarHadirRows, setDaftarHadirRows] = useState([]);
  const [isBelanjaRefreshing, setIsBelanjaRefreshing] = useState(false);
  const [isBelanjaDetailLoading, setIsBelanjaDetailLoading] = useState(false);
  const [showBelanjaDetailModal, setShowBelanjaDetailModal] = useState(false);
  const [showStokGudangDetailModal, setShowStokGudangDetailModal] = useState(false);
  const [isStokGudangDetailLoading, setIsStokGudangDetailLoading] = useState(false);
  const [stokGudangDetailMeta, setStokGudangDetailMeta] = useState({ category: '', miniCount: 0, realtimeCount: 0, namaCabang: '' });
  const [miniRows, setMiniRows] = useState([]);
  const [monitoringCounts, setMonitoringCounts] = useState({});
  const [refreshingMonitoringKey, setRefreshingMonitoringKey] = useState('');
  const [monitoringPreloadPayload, setMonitoringPreloadPayload] = useState({});
  const [trendRows, setTrendRows] = useState([]);
  const [trendWindowDays, setTrendWindowDays] = useState(30);
  const [trendWindowOffset, setTrendWindowOffset] = useState(0);
  const [selectedTrendCabang, setSelectedTrendCabang] = useState('ALL');
  const [selectedKomposisiCabang, setSelectedKomposisiCabang] = useState('');
  const [selectedMonitoringCabang, setSelectedMonitoringCabang] = useState('');
  const [selectedDaftarHadirCabang, setSelectedDaftarHadirCabang] = useState('');
  const [showMonitoringDetailModal, setShowMonitoringDetailModal] = useState(false);
  const [showDaftarHadirBulananModal, setShowDaftarHadirBulananModal] = useState(false);
  const [showSupervisiAbsensiModal, setShowSupervisiAbsensiModal] = useState(false);
  const [showDaftarHadirFotoModal, setShowDaftarHadirFotoModal] = useState(false);
  const [selectedDaftarHadirFotoFiles, setSelectedDaftarHadirFotoFiles] = useState([]);
  const [isAbsensiBulananLoading, setIsAbsensiBulananLoading] = useState(false);
  const [supervisiTanggal, setSupervisiTanggal] = useState('');
  const [supervisiRows, setSupervisiRows] = useState([]);
  const [supervisiLoading, setSupervisiLoading] = useState(false);
  const [supervisiSubmitting, setSupervisiSubmitting] = useState(false);
  const [supervisiStatusMap, setSupervisiStatusMap] = useState({});
  const [supervisiCheckedMap, setSupervisiCheckedMap] = useState({});
  const [showSupervisiKeteranganModal, setShowSupervisiKeteranganModal] = useState(false);
  const [selectedSupervisiRow, setSelectedSupervisiRow] = useState(null);
  const [supervisiLampiranMap, setSupervisiLampiranMap] = useState({});
  const [supervisiLampiranDraft, setSupervisiLampiranDraft] = useState([]);
  const [supervisiKeteranganDraft, setSupervisiKeteranganDraft] = useState('');
  const [supervisiInvalidCaptionIndexes, setSupervisiInvalidCaptionIndexes] = useState([]);
  const [showAbsenTerjadwalModal, setShowAbsenTerjadwalModal] = useState(false);
  const [absenTerjadwalForm, setAbsenTerjadwalForm] = useState({
    pegawaiKey: '',
    kategori: 'CUTI',
    tanggalMulai: '',
    tanggalSelesai: '',
    keterangan: '',
  });
  const [absenTerjadwalAttachments, setAbsenTerjadwalAttachments] = useState([]);
  const [absenTerjadwalInvalidCaptionIndexes, setAbsenTerjadwalInvalidCaptionIndexes] = useState([]);
  const [showAbsenTerjadwalKeteranganModal, setShowAbsenTerjadwalKeteranganModal] = useState(false);
  const [absenTerjadwalKeteranganDraft, setAbsenTerjadwalKeteranganDraft] = useState('');
  const [absenTerjadwalLampiranDraft, setAbsenTerjadwalLampiranDraft] = useState([]);
  const [absenTerjadwalPeriode, setAbsenTerjadwalPeriode] = useState({ tanggalAwal: '', tanggalAkhir: '' });
  const [absenTerjadwalFilter, setAbsenTerjadwalFilter] = useState('');
  const [absenTerjadwalPage, setAbsenTerjadwalPage] = useState(1);
  const [absenTerjadwalRows, setAbsenTerjadwalRows] = useState([]);
  const [absenTerjadwalPegawaiRows, setAbsenTerjadwalPegawaiRows] = useState([]);
  const [absenTerjadwalLoading, setAbsenTerjadwalLoading] = useState(false);
  const [absenTerjadwalSubmitting, setAbsenTerjadwalSubmitting] = useState(false);
  const [absenTerjadwalDeleting, setAbsenTerjadwalDeleting] = useState(false);
  const [showAbsenTerjadwalLampiranModal, setShowAbsenTerjadwalLampiranModal] = useState(false);
  const [selectedAbsenTerjadwalLampiranFiles, setSelectedAbsenTerjadwalLampiranFiles] = useState([]);
  const [absenTerjadwalDeleteTarget, setAbsenTerjadwalDeleteTarget] = useState(null);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [pendingAlertsTotal, setPendingAlertsTotal] = useState(0);
  const [pendingAlertRows, setPendingAlertRows] = useState([]);
  const notifDropdownRef = useRef(null);
  const selectedKomposisiCabangRef = useRef('');
  const getJakartaDateKey = () =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: JAKARTA_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  const getJakartaMonthStartKey = () => `${getJakartaDateKey().slice(0, 7)}-01`;

  const actorKodeCabang = String(authIdentity?.kode_cabang || "").trim();
  const actorNama = String(authIdentity?.nama || "").trim();
  const actorUsername = String(authIdentity?.username || '').trim();
  const actorUserLevel = Number(authIdentity?.user_level ?? 0);
  const cacheKey = `${DASHBOARD_MINICARD_CACHE_NS}:${actorKodeCabang || 'unknown'}`;
  const monitoringCacheKey = `${DASHBOARD_MONITORING_PRELOAD_CACHE_NS}:${actorKodeCabang || 'unknown'}`;
  const trendCacheKey = `${DASHBOARD_GRAFIK_TREN_CACHE_NS}:${actorKodeCabang || 'unknown'}`;
  const belanjaCacheKey = `${DASHBOARD_BELANJA_CACHE_NS}:${actorKodeCabang || 'unknown'}`;
  const isPusatUser = actorKodeCabang === 'C-1';
  const canOpenSupervisiAbsensi =
    (actorUserLevel === 7 && !isPusatUser)
    || (actorUserLevel >= 8 && isPusatUser)
    || actorUserLevel === 9;
  useEffect(() => {
    if (!isPusatUser) {
      setSelectedKomposisiCabang(actorKodeCabang || '');
      setSelectedMonitoringCabang(actorKodeCabang || '');
      setSelectedDaftarHadirCabang(actorKodeCabang || '');
      return;
    }
    setSelectedKomposisiCabang((prev) => String(prev || 'C-1').trim() || 'C-1');
    setSelectedMonitoringCabang((prev) => String(prev || 'C-1').trim() || 'C-1');
  }, [isPusatUser, actorKodeCabang]);

  useEffect(() => {
    selectedKomposisiCabangRef.current = selectedKomposisiCabang;
  }, [selectedKomposisiCabang]);

  const handleSyncedCabangChange = (rawValue) => {
    const value = String(rawValue || '').trim();
    const cabangValue = value === 'ALL' ? 'C-1' : (value || 'C-1');
    setSelectedTrendCabang(cabangValue === 'C-1' ? 'ALL' : cabangValue);
    setSelectedKomposisiCabang(cabangValue);
    setSelectedMonitoringCabang(cabangValue);
    refreshBelanjaYtd(cabangValue);
  };
  const applyMiniPayload = useCallback((payload) => {
    const rows = Array.isArray(payload?.rows) ? payload.rows : [];
    setMiniRows(rows);
    const pickByKode = (kode) => rows.find((x) => String(x?.kode_cabang || '').trim() === String(kode || '').trim()) || null;
    const selectedFromRows = isPusatUser
      ? (pickByKode('TOTAL') || rows[0] || null)
      : (pickByKode(actorKodeCabang) || rows[0] || null);
    const selectedPayload = payload?.selected && typeof payload.selected === 'object' ? payload.selected : null;
    setMiniSnapshot(selectedFromRows || selectedPayload || {});
    setMiniContext(payload?.context && typeof payload.context === 'object'
      ? payload.context
      : { last_year: new Date().getFullYear() - 1, day_of_year: 1, days_in_year: 365 });
  }, [actorKodeCabang, isPusatUser]);
  const refreshMiniCardFromServer = useCallback(async () => {
    const { data } = await api.get(`${backendPort}/api/main/dashboard_minicard`, {
      requestGroup: REQ_GROUP_DASHBOARD_MINICARD,
      timeout: 120000,
    });
    const payload = data || {};
    applyMiniPayload(payload);
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage?.setItem(cacheKey, JSON.stringify(payload));
      }
    } catch (_err) {}
    return payload;
  }, [applyMiniPayload, cacheKey]);
  const topFabStyle = {
    width: 42,
    height: 42,
    borderRadius: 999,
    border: '1px solid rgba(255,255,255,0.35)',
    background: 'rgba(15, 23, 42, 0.38)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.92,
    boxShadow: '0 8px 24px rgba(2,6,23,0.18)',
  };

  const handleRefreshDashboard = async () => {
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage?.removeItem(cacheKey);
        window.sessionStorage?.removeItem(trendCacheKey);
        window.sessionStorage?.removeItem(belanjaCacheKey);
        const storage = window.sessionStorage;
        const keysToRemove = [];
        for (let i = 0; i < storage.length; i += 1) {
          const key = String(storage.key(i) || '');
          if (key.startsWith(`${DASHBOARD_MONITORING_PRELOAD_CACHE_NS}:`)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach((key) => storage.removeItem(key));
        const prevRuntime = window.__laporan_runtime_state && typeof window.__laporan_runtime_state === 'object'
          ? window.__laporan_runtime_state
          : {};
        const prevCacheMap = prevRuntime?.cacheMap && typeof prevRuntime.cacheMap === 'object'
          ? prevRuntime.cacheMap
          : {};
        const nextCacheMap = {};
        Object.keys(prevCacheMap).forEach((k) => {
          if (!String(k || '').startsWith('MONITORING_BAN|')) {
            nextCacheMap[k] = prevCacheMap[k];
          }
        });
        window.__laporan_runtime_state = {
          ...prevRuntime,
          cacheMap: nextCacheMap,
        };
      }
    } catch (_err) {}
    try {
      await manualResyncGlobalOpsi?.({ requestGroup: 'master-sync-dashboard-refresh' });
    } catch (_err) {}
    await refreshBelanjaYtd();
    setReloadTick((prev) => prev + 1);
  };
  const handleStopPendingProcess = async () => {
    try {
      const total = Number(api.getPendingRequestCount() || 0);
      if (total <= 0) {
        showNotif('info', 'Tidak ada proses pending untuk dihentikan.');
        return;
      }
      api.cancelPendingRequests({ reason: 'Stopped by user' });
      showNotif('success', `STOP: ${total} proses pending berhasil dihentikan.`);
    } catch (err) {
      console.error(err);
      showNotif('error', 'STOP gagal dijalankan.');
    }
  };
  const loadPendingAlerts = async () => {
    const res = await api.get(`${backendPort}/api/main/dashboard_pending_notifications`, {
      requestGroup: REQ_GROUP_DASHBOARD_PENDING_ALERTS,
      timeout: 120000,
    });
    const rows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
    setPendingAlertRows(rows);
    setPendingAlertsTotal(Number(res?.data?.total || 0));
  };
  const normalizeBelanjaRows = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    const findValue = (label) => {
      const row = list.find((x) => String(x?.key || '').trim().toLowerCase() === label.toLowerCase()) || null;
      return Number(row?.value || 0);
    };
    return [
      { key: 'Pengadaan Ban', value: findValue('Pengadaan Ban'), color: '#0B3C5D' },
      { key: 'Retread', value: findValue('Retread'), color: '#0EA5A4' },
    ];
  };
  const refreshBelanjaYtd = async (kodeCabangOverride = '') => {
    setIsBelanjaRefreshing(true);
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage?.removeItem(belanjaCacheKey);
      }
    } catch (_err) {}
    const selectedCabang = String(
      kodeCabangOverride || selectedKomposisiCabang || (isPusatUser ? 'C-1' : actorKodeCabang)
    ).trim();
    try {
      const { data } = await api.get(`${backendPort}/api/main/dashboard_belanja_ban_ytd`, {
        params: isPusatUser ? { kode_cabang: selectedCabang || 'C-1' } : undefined,
        requestGroup: REQ_GROUP_DASHBOARD_BELANJA,
        timeout: 120000,
      });
      const normalized = normalizeBelanjaRows(data?.rows);
      setBelanjaYtd(normalized);
      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage?.setItem(belanjaCacheKey, JSON.stringify({ rows: normalized }));
        }
      } catch (_err) {}
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal load dashboard_belanja_ban_ytd:', err);
      }
      setBelanjaYtd(normalizeBelanjaRows([]));
    } finally {
      setIsBelanjaRefreshing(false);
    }
  };

  const applyMonitoringPreloadPayload = (payload) => {
    const rawCounts = payload?.counts && typeof payload.counts === 'object' ? payload.counts : {};
    const next = {};
    for (const kondisi of MONITORING_BAN_CONDITIONS) {
      const parsed = Number(rawCounts?.[kondisi]);
      next[kondisi] = Number.isFinite(parsed) ? parsed : 0;
    }
    setMonitoringCounts(next);
    setMonitoringPreloadPayload(payload && typeof payload === 'object' ? payload : {});
  };

  const handleRefreshMonitoringRow = async (kondisi) => {
    const key = String(kondisi || '').trim();
    setRefreshingMonitoringKey(key);
    try {
      const res = await api.get(`${backendPort}/api/main/dashboard_monitoring_ban_count`, {
        params: {
          condition: key,
          suplier: key === 'Ban di pabrik lebih dari 3 bulan' ? '' : undefined,
        },
        requestGroup: REQ_GROUP_DASHBOARD_MONITORING_BAN,
        timeout: 120000,
      });
      const rawCount = res?.data?.count;
      const refreshedRows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      setMonitoringCounts((prev) => ({ ...prev, [key]: rawCount }));
      setMonitoringPreloadPayload((prev) => {
        const prevPayload = prev && typeof prev === 'object' ? prev : {};
        const prevTables = prevPayload?.tables && typeof prevPayload.tables === 'object' ? prevPayload.tables : {};
        const prevCounts = prevPayload?.counts && typeof prevPayload.counts === 'object' ? prevPayload.counts : {};
        const nextPayload = {
          ...prevPayload,
          tables: {
            ...prevTables,
            [key]: refreshedRows,
          },
          counts: {
            ...prevCounts,
            [key]: Number(rawCount || 0),
          },
        };
        try {
          if (typeof window !== 'undefined') {
            window.sessionStorage?.setItem(monitoringCacheKey, JSON.stringify(nextPayload || {}));
          }
        } catch (_err) {}
        return nextPayload;
      });
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal refresh monitoring ban:', err);
      }
      setMonitoringCounts((prev) => ({ ...prev, [key]: 'nodata' }));
    } finally {
      setRefreshingMonitoringKey('');
    }
  };

  useEffect(() => {
    const unsubscribe = api.subscribePendingRequests((count) => {
      setPendingRequestCount(Number(count || 0));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadMiniCards = async () => {
      if (isMounted) setIsRefreshing(true);
      try {
        let miniLoadedFromCache = false;
        try {
          const cachedRaw = (typeof window !== 'undefined')
            ? window.sessionStorage?.getItem(cacheKey)
            : '';
          if (cachedRaw) {
            const cached = JSON.parse(cachedRaw);
            if (cached && typeof cached === 'object') {
              applyMiniPayload(cached);
              miniLoadedFromCache = true;
            }
          }
        } catch (_err) {}

        setMonitoringCounts({});
        setMonitoringPreloadPayload({});
        try {
          if (typeof window !== 'undefined') {
            window.sessionStorage?.removeItem(belanjaCacheKey);
            const storage = window.sessionStorage;
            const keysToRemove = [];
            for (let i = 0; i < storage.length; i += 1) {
              const key = String(storage.key(i) || '');
              if (key.startsWith(`${DASHBOARD_MONITORING_PRELOAD_CACHE_NS}:`)) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach((key) => storage.removeItem(key));
            const prevRuntime = window.__laporan_runtime_state && typeof window.__laporan_runtime_state === 'object'
              ? window.__laporan_runtime_state
              : {};
            const prevCacheMap = prevRuntime?.cacheMap && typeof prevRuntime.cacheMap === 'object'
              ? prevRuntime.cacheMap
              : {};
            const nextCacheMap = {};
            Object.keys(prevCacheMap).forEach((k) => {
              if (!String(k || '').startsWith('MONITORING_BAN|')) {
                nextCacheMap[k] = prevCacheMap[k];
              }
            });
            window.__laporan_runtime_state = {
              ...prevRuntime,
              cacheMap: nextCacheMap,
            };
          }
        } catch (_err) {}
        const pendingPromise = loadPendingAlerts();
        const selectedBelanjaCabang = String(
          selectedKomposisiCabangRef.current || (actorKodeCabang === 'C-1' ? 'C-1' : actorKodeCabang)
        ).trim();
        const belanjaPromise = api.get(`${backendPort}/api/main/dashboard_belanja_ban_ytd`, {
          params: actorKodeCabang === 'C-1' ? { kode_cabang: selectedBelanjaCabang || 'C-1' } : undefined,
          requestGroup: REQ_GROUP_DASHBOARD_BELANJA,
          timeout: 120000,
        });
        const miniPromise = miniLoadedFromCache
          ? Promise.resolve(null)
          : api.get(`${backendPort}/api/main/dashboard_minicard`, {
              requestGroup: REQ_GROUP_DASHBOARD_MINICARD,
              timeout: 120000,
            });
        const monitoringBanPromise = api.get(`${backendPort}/api/main/dashboard_monitoring_ban_preload`, {
          requestGroup: REQ_GROUP_DASHBOARD_MONITORING_BAN,
          timeout: 120000,
        });
        const daftarHadirPromise = api.get(`${backendPort}/api/main/dashboard_daftar_hadir_pegawai`, {
          requestGroup: REQ_GROUP_DASHBOARD_DAFTAR_HADIR,
          timeout: 120000,
        });
        const [pendingRes, belanjaRes, miniRes, monitoringBanRes, daftarHadirRes] = await Promise.allSettled([pendingPromise, belanjaPromise, miniPromise, monitoringBanPromise, daftarHadirPromise]);

        if (!isMounted) return;

        if (pendingRes.status === 'rejected') {
          const err = pendingRes.reason;
          if (!api.isCanceledError?.(err)) {
            console.error('Gagal load pending dashboard alerts:', err);
          }
        }

        if (belanjaRes.status === 'fulfilled') {
          const normalized = normalizeBelanjaRows(belanjaRes.value?.data?.rows);
          setBelanjaYtd(normalized);
          try {
            if (typeof window !== 'undefined') {
              window.sessionStorage?.setItem(belanjaCacheKey, JSON.stringify({ rows: normalized }));
            }
          } catch (_err) {}
        } else if (belanjaRes.status === 'rejected') {
          const err = belanjaRes.reason;
          if (!api.isCanceledError?.(err)) {
            console.error('Gagal load dashboard_belanja_ban_ytd:', err);
          }
          setBelanjaYtd(normalizeBelanjaRows([]));
        }

        if (!miniLoadedFromCache && miniRes.status === 'fulfilled') {
          const payload = miniRes.value?.data || {};
          applyMiniPayload(payload);
          try {
            if (typeof window !== 'undefined') {
              window.sessionStorage?.setItem(cacheKey, JSON.stringify(payload || {}));
            }
          } catch (_err) {}
        } else if (!miniLoadedFromCache) {
          const err = miniRes.reason;
          if (!api.isCanceledError?.(err)) {
            console.error('Gagal load dashboard_minicard:', err);
          }
        }

        if (monitoringBanRes.status === 'fulfilled') {
          const payload = monitoringBanRes.value?.data || {};
          applyMonitoringPreloadPayload(payload);
          try {
            if (typeof window !== 'undefined') {
              window.sessionStorage?.removeItem(monitoringCacheKey);
              window.sessionStorage?.setItem(monitoringCacheKey, JSON.stringify(payload || {}));
            }
          } catch (_err) {}
        } else if (monitoringBanRes.status === 'rejected') {
          const err = monitoringBanRes.reason;
          if (!api.isCanceledError?.(err)) {
            console.error('Gagal load dashboard_monitoring_ban_preload:', err);
          }
          setMonitoringCounts({});
          setMonitoringPreloadPayload({});
        }

        if (daftarHadirRes.status === 'fulfilled') {
          const rows = Array.isArray(daftarHadirRes.value?.data?.rows) ? daftarHadirRes.value.data.rows : [];
          setDaftarHadirRows(rows);
        } else if (daftarHadirRes.status === 'rejected') {
          const err = daftarHadirRes.reason;
          if (!api.isCanceledError?.(err)) {
            console.error('Gagal load dashboard_daftar_hadir_pegawai:', err);
          }
          setDaftarHadirRows([]);
        }

      } finally {
        if (isMounted) setIsRefreshing(false);
      }
    };

    loadMiniCards();
    return () => {
      isMounted = false;
      api.cancelPendingRequests({
        group: REQ_GROUP_DASHBOARD_MINICARD,
        reason: 'Dashboard unmount',
      });
      api.cancelPendingRequests({
        group: REQ_GROUP_DASHBOARD_MONITORING_BAN,
        reason: 'Dashboard unmount',
      });
      api.cancelPendingRequests({
        group: REQ_GROUP_DASHBOARD_PENDING_ALERTS,
        reason: 'Dashboard unmount',
      });
      api.cancelPendingRequests({
        group: REQ_GROUP_DASHBOARD_BELANJA,
        reason: 'Dashboard unmount',
      });
      api.cancelPendingRequests({
        group: REQ_GROUP_DASHBOARD_BELANJA_DETAIL,
        reason: 'Dashboard unmount',
      });
      api.cancelPendingRequests({
        group: REQ_GROUP_DASHBOARD_DAFTAR_HADIR,
        reason: 'Dashboard unmount',
      });
      api.cancelPendingRequests({
        group: REQ_GROUP_DASHBOARD_DAFTAR_HADIR_BULANAN,
        reason: 'Dashboard unmount',
      });
    };
  }, [cacheKey, actorKodeCabang, monitoringCacheKey, belanjaCacheKey, reloadTick, applyMiniPayload]);
  useEffect(() => {
    let isMounted = true;
    const loadTrendData = async () => {
      const isPusatAllMode = isPusatUser && selectedTrendCabang === 'ALL';
      const canUseDailyCache = (!isPusatUser) || isPusatAllMode;
      const canWriteDailyCache = (!isPusatUser) || isPusatAllMode;
      let loadedFromCache = false;
      if (canUseDailyCache) {
        try {
          const todayKey = getJakartaDateKey();
          const trendCachedRaw = (typeof window !== 'undefined')
            ? window.sessionStorage?.getItem(trendCacheKey)
            : '';
          if (trendCachedRaw) {
            const trendCached = JSON.parse(trendCachedRaw);
            const cacheDate = String(trendCached?.cache_date || '').trim();
            const cachedPayload = trendCached?.payload && typeof trendCached.payload === 'object' ? trendCached.payload : null;
            const cachedRows = Array.isArray(cachedPayload?.rows) ? cachedPayload.rows : [];
            if (cacheDate && cacheDate === todayKey && cachedRows.length > 0) {
              setTrendRows(cachedRows);
              loadedFromCache = true;
            }
          }
        } catch (_err) {}
      }
      if (loadedFromCache) return;
      try {
        const res = await api.get(`${backendPort}/api/main/dashboard_grafik_tren_preload`, {
          params: isPusatUser ? { kode_cabang: selectedTrendCabang || 'ALL' } : undefined,
          requestGroup: REQ_GROUP_DASHBOARD_GRAFIK_TREN,
          timeout: 120000,
        });
        if (!isMounted) return;
        const payload = res?.data || {};
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];
        setTrendRows(rows);
        if (canWriteDailyCache) {
          try {
            if (typeof window !== 'undefined') {
              const todayKey = getJakartaDateKey();
              window.sessionStorage?.removeItem(trendCacheKey);
              window.sessionStorage?.setItem(trendCacheKey, JSON.stringify({ cache_date: todayKey, payload }));
            }
          } catch (_err) {}
        }
      } catch (err) {
        if (!isMounted) return;
        if (!api.isCanceledError?.(err)) {
          console.error('Gagal load dashboard_grafik_tren_preload:', err);
        }
        setTrendRows([]);
      }
    };
    loadTrendData();
    return () => {
      isMounted = false;
      api.cancelPendingRequests({
        group: REQ_GROUP_DASHBOARD_GRAFIK_TREN,
        reason: 'Dashboard trend unmount',
      });
    };
  }, [isPusatUser, selectedTrendCabang, trendCacheKey]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!notifDropdownRef.current) return;
      if (!notifDropdownRef.current.contains(event.target)) setIsNotifOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const num = (v) => Number(v || 0);
  const fmtNum = (v) => Number(v || 0).toLocaleString('id-ID');
  const fmtPct = (v) => `${Number(v || 0).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
  const fmtDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const fmtTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };
  const fmtTodaySubtitle = () =>
    new Intl.DateTimeFormat('id-ID', {
      timeZone: JAKARTA_TIMEZONE,
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(new Date());
  const formatTanggalPanjang = (value) => {
    const raw = String(value || '').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '-';
    const [year, month, day] = raw.split('-').map((x) => Number(x));
    const date = new Date(Date.UTC(year, Math.max(0, month - 1), day));
    return new Intl.DateTimeFormat('id-ID', {
      timeZone: JAKARTA_TIMEZONE,
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(date);
  };
  const getJakartaMonthKey = () =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: JAKARTA_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
    }).format(new Date());
  const formatMonthLabel = (value) => {
    const raw = String(value || '').trim();
    if (!/^\d{4}-\d{2}$/.test(raw)) return raw || '-';
    const [year, month] = raw.split('-').map((x) => Number(x));
    const date = new Date(Date.UTC(year, Math.max(0, month - 1), 1));
    return new Intl.DateTimeFormat('id-ID', {
      timeZone: JAKARTA_TIMEZONE,
      month: 'long',
      year: 'numeric',
    }).format(date);
  };
  const signedDeltaText = (delta) => `${delta >= 0 ? '' : '-'}${fmtNum(Math.abs(delta))}`;
  const deltaPct = (current, baseline) => {
    if (!Number.isFinite(Number(baseline)) || Number(baseline) === 0) return 0;
    return ((Number(current) - Number(baseline)) / Number(baseline)) * 100;
  };
  const mapNamaCabang = useMemo(() => {
    const m = new Map();
    const list = Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : [];
    for (const row of list) {
      const kode = String(row?.kode_cabang || '').trim();
      const nama = String(row?.nama_cabang || '').trim();
      if (!kode) continue;
      m.set(kode, nama || kode);
    }
    return m;
  }, [globalOpsi?.opsiCabang]);
  const trendCabangOptions = useMemo(() => {
    const opsiCabang = Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : [];
    const base = [{ value: 'ALL', label: 'Semua Cabang' }];
    const rows = opsiCabang
      .map((row) => {
        const kode = String(row?.kode_cabang || '').trim();
        const nama = String(row?.nama_cabang || '').trim();
        return { kode, nama };
      })
      .filter((row) => row.kode && row.kode !== 'C-1')
      .sort((a, b) => a.kode.localeCompare(b.kode))
      .map((row) => ({ value: row.kode, label: row.nama || row.kode }));
    return [...base, ...rows];
  }, [globalOpsi?.opsiCabang]);
  const monitoringCabangOptions = useMemo(() => {
    if (!isPusatUser) return [];
    const opsiCabang = Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : [];
    const rows = opsiCabang
      .map((row) => {
        const kode = String(row?.kode_cabang || '').trim();
        const nama = String(row?.nama_cabang || '').trim();
        return { kode, nama };
      })
      .filter((row) => row.kode)
      .sort((a, b) => a.kode.localeCompare(b.kode, 'id', { sensitivity: 'base', numeric: true }))
      .map((row) => ({ value: row.kode, label: row.nama || row.kode }));
    const withoutPusat = rows.filter((x) => x.value !== 'C-1');
    return [{ value: 'C-1', label: 'Semua Cabang' }, ...withoutPusat];
  }, [isPusatUser, globalOpsi?.opsiCabang]);
  const daftarHadirCabangOptions = useMemo(() => {
    if (!isPusatUser) return [];
    const opsiCabang = Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : [];
    const rows = opsiCabang
      .map((row) => {
        const kode = String(row?.kode_cabang || '').trim();
        const nama = String(row?.nama_cabang || '').trim();
        return { value: kode, label: nama || kode };
      })
      .filter((row) => row.value && row.value !== 'C-1')
      .sort((a, b) => a.value.localeCompare(b.value, 'id', { sensitivity: 'base', numeric: true }));
    return [{ value: 'C-1', label: 'Semua Cabang' }, ...rows];
  }, [isPusatUser, globalOpsi?.opsiCabang]);
  const normalizedCabangName = useMemo(() => {
    const kodeCabang = actorKodeCabang;
    if (!kodeCabang) return '-';
    if (kodeCabang === 'C-1') return 'Semua Cabang';
    return mapNamaCabang.get(kodeCabang) || kodeCabang;
  }, [actorKodeCabang, mapNamaCabang]);
  useEffect(() => {
    if (!isPusatUser) return;
    const defaultCabang = 'C-1';
    setSelectedDaftarHadirCabang((prev) => {
      const current = String(prev || '').trim();
      if (current && daftarHadirCabangOptions.some((opt) => opt.value === current)) return current;
      return defaultCabang;
    });
  }, [isPusatUser, daftarHadirCabangOptions]);
  const daftarHadirSubtitle = useMemo(() => fmtTodaySubtitle(), []);
  const supervisiTargetCabangKode = useMemo(
    () => (isPusatUser ? String(selectedDaftarHadirCabang || 'C-1').trim() : actorKodeCabang),
    [isPusatUser, selectedDaftarHadirCabang, actorKodeCabang]
  );
  const supervisiTargetCabangName = useMemo(() => {
    const kodeCabang = String(supervisiTargetCabangKode || '').trim();
    if (!kodeCabang || kodeCabang === 'C-1') return 'Semua Cabang';
    return mapNamaCabang.get(kodeCabang) || kodeCabang;
  }, [supervisiTargetCabangKode, mapNamaCabang]);
  const normalizeSupervisiStatus = (value) => {
    const raw = String(value || '').trim().toUpperCase();
    if (raw === 'IZIN') return 'IJIN';
    return SUPERVISI_STATUS_OPTIONS.includes(raw) ? raw : '';
  };
  const formatSupervisiJamAbsen = (value) => {
    const raw = String(value || '').trim();
    if (!raw || raw === '-') return '-';
    const match = raw.match(/^(\d{2}):(\d{2})/);
    if (match) return `${match[1]}:${match[2]}`;
    return raw;
  };
  const sanitizeSupervisiLampiranItem = (item) => {
    if (!item || typeof item !== 'object') return null;
    const name = String(item?.name || item?.file?.name || '').trim();
    const file = typeof item?.file === 'string' ? String(item.file).trim() : '';
    const path = String(item?.path || item?.url || '').trim();
    const type = String(item?.type || item?.file?.type || '').trim();
    const size = Number(item?.size || item?.file?.size || 0);
    const caption = String(item?.caption || '').trim();
    const uploaded_at = item?.uploaded_at || null;
    if (!name && !file && !path) return null;
    return {
      name,
      file,
      path,
      type,
      size: Number.isFinite(size) ? size : 0,
      caption,
      uploaded_at,
    };
  };
  const daftarHadirTodayRows = useMemo(() => {
    const mappedRows = (Array.isArray(daftarHadirRows) ? daftarHadirRows : []).map((row) => ({
      cabang: String(row?.Cabang || row?.cabang || '').trim() || '-',
      nama: String(row?.Nama || row?.nama || '').trim() || '-',
      statusAbsen: String(row?.['Status Absen'] || row?.status_absen || row?.statusAbsen || '').trim() || '-',
      jamAbsen: String(row?.['Jam Absen'] || row?.jam_absen || row?.jamAbsen || '').trim() || '-',
      lokasi: String(row?.Lokasi || row?.lokasi || '').trim() || '-',
      foto: String(row?.Foto || row?.foto || '').trim() || '',
    }));
    if (!isPusatUser) return mappedRows;
    const selected = String(selectedDaftarHadirCabang || '').trim();
    if (!selected || selected === 'C-1') return mappedRows;
    const selectedLabel = String(mapNamaCabang.get(selected) || selected).trim().toUpperCase();
    return mappedRows.filter((row) => String(row.cabang || '').trim().toUpperCase() === selectedLabel);
  }, [daftarHadirRows, isPusatUser, selectedDaftarHadirCabang, mapNamaCabang]);
  const absenTerjadwalPegawaiOptions = useMemo(() => {
    const unique = new Map();
    for (const row of absenTerjadwalPegawaiRows) {
      const username = String(row?.username || '').trim();
      const nama = String(row?.nama || '').trim();
      const cabang = String(row?.nama_cabang || row?.cabang || row?.kode_cabang || '').trim();
      const kodeCabang = String(row?.kode_cabang || '').trim();
      if (!nama || nama === '-') continue;
      const key = username || `${nama}__${kodeCabang || cabang || '-'}`;
      if (!unique.has(key)) unique.set(key, { key, username, nama, kodeCabang, cabang: cabang || kodeCabang || '-' });
    }
    return [...unique.values()].sort((left, right) => {
      const byNama = String(left.nama).localeCompare(String(right.nama), 'id', { sensitivity: 'base' });
      if (byNama !== 0) return byNama;
      return String(left.cabang).localeCompare(String(right.cabang), 'id', { sensitivity: 'base' });
    });
  }, [absenTerjadwalPegawaiRows]);
  const selectedAbsenTerjadwalPegawai = useMemo(
    () => absenTerjadwalPegawaiOptions.find((row) => row.key === absenTerjadwalForm.pegawaiKey) || null,
    [absenTerjadwalPegawaiOptions, absenTerjadwalForm.pegawaiKey]
  );
  const filteredAbsenTerjadwalRows = useMemo(() => {
    const awal = String(absenTerjadwalPeriode.tanggalAwal || '').trim();
    const akhir = String(absenTerjadwalPeriode.tanggalAkhir || '').trim();
    const keyword = String(absenTerjadwalFilter || '').trim().toLocaleLowerCase('id-ID');
    return absenTerjadwalRows.filter((row) => {
      const mulai = String(row?.tanggalMulai || '').trim();
      const selesai = String(row?.tanggalSelesai || '').trim();
      if (awal && selesai && selesai < awal) return false;
      if (akhir && mulai && mulai > akhir) return false;
      if (!keyword) return true;
      const searchableText = [
        row?.nama,
        row?.cabang,
        row?.statusAbsen,
        row?.tanggalMulai,
        row?.tanggalSelesai,
        row?.keterangan,
        ...(Array.isArray(row?.lampiran) ? row.lampiran.flatMap((item) => [item?.name, item?.caption]) : []),
      ].map((value) => String(value || '').trim().toLocaleLowerCase('id-ID')).join(' ');
      return searchableText.includes(keyword);
    });
  }, [absenTerjadwalRows, absenTerjadwalPeriode, absenTerjadwalFilter]);
  const absenTerjadwalTotalPages = Math.max(1, Math.ceil(filteredAbsenTerjadwalRows.length / 10));
  const absenTerjadwalCurrentPage = Math.min(absenTerjadwalPage, absenTerjadwalTotalPages);
  const paginatedAbsenTerjadwalRows = useMemo(() => {
    const start = (absenTerjadwalCurrentPage - 1) * 10;
    return filteredAbsenTerjadwalRows.slice(start, start + 10);
  }, [filteredAbsenTerjadwalRows, absenTerjadwalCurrentPage]);
  useEffect(() => {
    setAbsenTerjadwalPage(1);
  }, [absenTerjadwalFilter, absenTerjadwalPeriode.tanggalAwal, absenTerjadwalPeriode.tanggalAkhir]);
  useEffect(() => {
    if (absenTerjadwalPage > absenTerjadwalTotalPages) setAbsenTerjadwalPage(absenTerjadwalTotalPages);
  }, [absenTerjadwalPage, absenTerjadwalTotalPages]);
  const hasBelumAbsenRow = useMemo(
    () => daftarHadirTodayRows.some((row) => String(row?.statusAbsen || '').trim().toUpperCase() === 'BELUM ABSEN'),
    [daftarHadirTodayRows]
  );
  useEffect(() => {
    if (!supervisiTanggal) {
      setSupervisiTanggal(getJakartaDateKey());
    }
  }, []);
  const loadSupervisiAbsensi = async (tanggalParam) => {
    const tanggal = String(tanggalParam || supervisiTanggal || getJakartaDateKey()).trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(tanggal)) return;
    setSupervisiLoading(true);
    try {
      const kodeCabangRaw = String(supervisiTargetCabangKode || '').trim();
      const { data } = await api.get(`${backendPort}/api/main/dashboard_supervisi_absensi`, {
        params: {
          tanggal,
          kode_cabang: kodeCabangRaw || undefined,
        },
        requestGroup: REQ_GROUP_DASHBOARD_SUPERVISI_ABSENSI,
        timeout: 120000,
      });
      const rawRows = Array.isArray(data?.rows) ? data.rows : [];
      const rawRowMap = new Map(
        rawRows.map((row, index) => [
          String(row?.username || row?.nama || `row-${index}`),
          row,
        ])
      );
      const rows = rawRows.map((row, index) => ({
        key: String(row?.username || row?.nama || `row-${index}`),
        username: String(row?.username || row?.nama || `row-${index}`),
        nama: String(row?.nama || row?.username || '-').trim() || '-',
        jamAbsen: formatSupervisiJamAbsen(row?.jam_absen || row?.jamAbsen || ''),
        lokasi: String(row?.lokasi || '').trim() || '-',
        statusAwal: String(row?.status_absen || row?.statusAbsen || '').trim().toUpperCase() || 'BELUM ABSEN',
        foto: String(row?.foto || row?.Foto || '').trim(),
      }));
      const nextStatusMap = {};
      const nextLampiranMap = {};
      rows.forEach((row) => {
        nextStatusMap[row.key] = normalizeSupervisiStatus(row.statusAwal);
        const matchedRaw = rawRowMap.get(row.username) || null;
        const lampiran = (Array.isArray(matchedRaw?.lampiran) ? matchedRaw.lampiran : [])
          .map((item) => sanitizeSupervisiLampiranItem(item))
          .filter(Boolean);
        nextLampiranMap[row.key] = {
          attachments: lampiran,
          keterangan: String(matchedRaw?.keterangan || '').trim(),
        };
      });
      setSupervisiRows(rows);
      setSupervisiStatusMap(nextStatusMap);
      setSupervisiCheckedMap({});
      setSupervisiLampiranMap(nextLampiranMap);
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal load dashboard_supervisi_absensi:', err);
        showNotif('error', err?.response?.data?.message || 'Gagal memuat data supervisi absensi.');
      }
    } finally {
      setSupervisiLoading(false);
    }
  };
  const handleOpenAbsensiBulanan = async () => {
    const bulan = getJakartaMonthKey();
    const kodeCabangRaw = isPusatUser ? String(selectedDaftarHadirCabang || 'C-1').trim() : actorKodeCabang;
    const kodeCabang = kodeCabangRaw === 'C-1' ? '' : kodeCabangRaw;
    const namaCabang = !kodeCabangRaw || kodeCabangRaw === 'C-1'
      ? 'Semua Cabang'
      : (mapNamaCabang.get(kodeCabangRaw) || kodeCabangRaw);
    setIsAbsensiBulananLoading(true);
    try {
      const { data } = await api.get(`${backendPort}/api/main/dashboard_daftar_hadir_bulanan`, {
        params: {
          kode_cabang: kodeCabang,
          bulan,
        },
        requestGroup: REQ_GROUP_DASHBOARD_DAFTAR_HADIR_BULANAN,
        timeout: 120000,
      });
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      if (typeof window !== 'undefined') {
        const prev = (window.__laporan_runtime_state && typeof window.__laporan_runtime_state === 'object')
          ? window.__laporan_runtime_state
          : {};
        const reportKey = `DASHBOARD_DAFTAR_HADIR_BULANAN|${bulan}|${kodeCabangRaw || 'ALL'}`;
        window.__laporan_runtime_state = {
          ...prev,
          viewMeta: {
            title: `ABSENSI BULANAN - ${namaCabang}`,
            tanggal: formatMonthLabel(bulan),
            report_key: reportKey,
          },
          cacheMap: {
            ...(prev?.cacheMap && typeof prev.cacheMap === 'object' ? prev.cacheMap : {}),
            [reportKey]: { rows },
          },
        };
      }
      setShowDaftarHadirBulananModal(true);
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal load dashboard_daftar_hadir_bulanan:', err);
        showNotif('error', err?.response?.data?.message || 'Gagal memuat absensi bulanan.');
      }
    } finally {
      setIsAbsensiBulananLoading(false);
    }
  };
  const handleOpenBelanjaDetail = async (item) => {
    const label = String(item?.key || '').trim();
    const laporanMap = {
      'Pengadaan Ban': 'pembelian_ban',
      Retread: 'vulkanisir',
    };
    const laporan = laporanMap[label] || '';
    if (!laporan) return;
    const kodeCabangRaw = String(
      isPusatUser ? (selectedKomposisiCabang || 'C-1') : actorKodeCabang
    ).trim();
    const isAllCabang = !kodeCabangRaw || kodeCabangRaw === 'C-1' || kodeCabangRaw === 'ALL';
    const namaCabang = isAllCabang
      ? 'Semua Cabang'
      : (mapNamaCabang.get(kodeCabangRaw) || kodeCabangRaw);
    const bulan = getJakartaMonthKey();
    const title = `BELANJA YTD - ${label} - ${namaCabang}`;
    setIsBelanjaDetailLoading(true);
    try {
      const { data } = await api.get(`${backendPort}/api/main/dashboard_belanja_ban_ytd_detail`, {
        params: {
          kode_cabang: isPusatUser ? kodeCabangRaw : undefined,
          laporan,
        },
        requestGroup: REQ_GROUP_DASHBOARD_BELANJA_DETAIL,
        timeout: 120000,
      });
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      if (typeof window !== 'undefined') {
        const prev = (window.__laporan_runtime_state && typeof window.__laporan_runtime_state === 'object')
          ? window.__laporan_runtime_state
          : {};
        const reportKey = `DASHBOARD_BELANJA_YTD|${laporan}|${kodeCabangRaw || 'ALL'}|${bulan}`;
        window.__laporan_runtime_state = {
          ...prev,
          viewMeta: {
            title,
            tanggal: `Year To Date ${new Date().getFullYear()}`,
            report_key: reportKey,
          },
          cacheMap: {
            ...(prev?.cacheMap && typeof prev.cacheMap === 'object' ? prev.cacheMap : {}),
            [reportKey]: { rows },
          },
        };
      }
      setShowBelanjaDetailModal(true);
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal load dashboard_belanja_ban_ytd_detail:', err);
        showNotif('error', err?.response?.data?.message || 'Gagal memuat detail Belanja Year To Date.');
      }
    } finally {
      setIsBelanjaDetailLoading(false);
    }
  };
  const handleOpenStokGudangDetail = async (category) => {
    const categoryKey = String(category || '').trim().toLowerCase();
    const categoryLabelMap = { ori: 'ORI', retread: 'RETREAD', casing: 'CASING', afkir: 'AFKIR' };
    const categoryLabel = categoryLabelMap[categoryKey];
    if (!categoryLabel) return;
    const kodeCabangRaw = String(
      isPusatUser ? (selectedKomposisiCabang || 'C-1') : actorKodeCabang
    ).trim();
    const isAllCabang = !kodeCabangRaw || kodeCabangRaw === 'C-1' || kodeCabangRaw === 'ALL';
    const namaCabang = isAllCabang
      ? 'Semua Cabang'
      : (mapNamaCabang.get(kodeCabangRaw) || kodeCabangRaw);
    const miniFieldMap = { ori: 'g_ori', retread: 'g_retread', casing: 'g_casing', afkir: 'g_afkir' };
    setIsStokGudangDetailLoading(true);
    try {
      const miniPayload = await refreshMiniCardFromServer();
      const freshMiniRows = Array.isArray(miniPayload?.rows) ? miniPayload.rows : [];
      const freshMiniRow = freshMiniRows.find((row) => String(row?.kode_cabang || '').trim() === (isAllCabang ? 'TOTAL' : kodeCabangRaw)) || {};
      const miniCount = Number(freshMiniRow?.[miniFieldMap[categoryKey]] || 0);
      const { data } = await api.get(`${backendPort}/api/main/dashboard_stok_gudang_detail`, {
        params: {
          kategori: categoryKey,
          ...(isPusatUser ? { kode_cabang: kodeCabangRaw } : {}),
        },
        requestGroup: 'dashboard-stok-gudang-detail-v1',
        timeout: 120000,
      });
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const tanggal = String(data?.tanggal || getJakartaDateKey()).trim();
      const reportKey = `STOK_GUDANG|DASHBOARD|${categoryKey}|${isAllCabang ? 'ALL' : kodeCabangRaw}|${tanggal}`;
      if (typeof window !== 'undefined') {
        const prev = (window.__laporan_runtime_state && typeof window.__laporan_runtime_state === 'object')
          ? window.__laporan_runtime_state
          : {};
        window.__laporan_runtime_state = {
          ...prev,
          viewMeta: {
            title: `STOK GUDANG - ${categoryLabel} - ${namaCabang}`,
            tanggal,
            report_key: reportKey,
          },
          cacheMap: {
            ...(prev?.cacheMap && typeof prev.cacheMap === 'object' ? prev.cacheMap : {}),
            [reportKey]: { rows },
          },
        };
      }
      setStokGudangDetailMeta({
        category: categoryLabel,
        miniCount,
        realtimeCount: Number(data?.total ?? rows.length),
        namaCabang,
      });
      setShowStokGudangDetailModal(true);
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal memuat detail stok gudang:', err);
        showNotif('error', err?.response?.data?.message || 'Gagal memuat detail stok gudang.');
      }
    } finally {
      setIsStokGudangDetailLoading(false);
    }
  };
  const handleOpenVerifikasiAbsensi = () => {
    const todayKey = getJakartaDateKey();
    setSupervisiTanggal(todayKey);
    setShowSupervisiAbsensiModal(true);
  };
  const handleOpenAbsenTerjadwal = () => {
    const todayKey = getJakartaDateKey();
    const monthStartKey = getJakartaMonthStartKey();
    setAbsenTerjadwalForm((prev) => ({
      ...prev,
      tanggalMulai: prev.tanggalMulai || todayKey,
      tanggalSelesai: prev.tanggalSelesai || todayKey,
    }));
    setAbsenTerjadwalPeriode((prev) => ({
      tanggalAwal: prev.tanggalAwal || monthStartKey,
      tanggalAkhir: prev.tanggalAkhir || todayKey,
    }));
    setShowAbsenTerjadwalModal(true);
  };
  const updateAbsenTerjadwalForm = (field, value) => {
    setAbsenTerjadwalForm((prev) => ({ ...prev, [field]: value }));
  };
  const collectAbsenTerjadwalInvalidCaptionIndexes = () => (
    (Array.isArray(absenTerjadwalAttachments) ? absenTerjadwalAttachments : [])
      .map((item, idx) => (String(item?.caption || '').trim() ? -1 : idx))
      .filter((idx) => idx >= 0)
  );
  const loadAbsenTerjadwal = async (periodeOverride = null) => {
    const periode = periodeOverride || absenTerjadwalPeriode;
    const tanggalAwal = String(periode?.tanggalAwal || getJakartaMonthStartKey()).trim();
    const tanggalAkhir = String(periode?.tanggalAkhir || getJakartaDateKey()).trim();
    if (tanggalAwal && tanggalAkhir && tanggalAkhir < tanggalAwal) {
      showNotif('error', 'Tanggal Akhir tidak boleh lebih awal dari Tanggal Awal.');
      return;
    }
    setAbsenTerjadwalLoading(true);
    try {
      const { data } = await api.get(`${backendPort}/api/main/dashboard_absen_terjadwal`, {
        params: {
          tanggal_awal: tanggalAwal,
          tanggal_akhir: tanggalAkhir,
          kode_cabang: String(supervisiTargetCabangKode || '').trim() || undefined,
        },
        requestGroup: REQ_GROUP_DASHBOARD_ABSEN_TERJADWAL,
        timeout: 120000,
      });
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setAbsenTerjadwalRows(rows.map((row) => ({
        id: row?.id,
        username: String(row?.username || '').trim(),
        nama: String(row?.nama || row?.username || '-').trim() || '-',
        kodeCabang: String(row?.kode_cabang || '').trim(),
        cabang: String(row?.nama_cabang || row?.kode_cabang || '-').trim() || '-',
        statusAbsen: String(row?.kategori || '').trim().toUpperCase(),
        tanggalMulai: String(row?.tanggal_mulai || '').trim(),
        tanggalSelesai: String(row?.tanggal_selesai || '').trim(),
        keterangan: String(row?.keterangan || '').trim(),
        lampiran: Array.isArray(row?.lampiran) ? row.lampiran : [],
      })));
      setAbsenTerjadwalPegawaiRows(Array.isArray(data?.pegawai_options) ? data.pegawai_options : []);
      setAbsenTerjadwalPeriode({ tanggalAwal, tanggalAkhir });
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal load dashboard_absen_terjadwal:', err);
        showNotif('error', err?.response?.data?.message || 'Gagal memuat daftar absen terjadwal.');
      }
      setAbsenTerjadwalRows([]);
    } finally {
      setAbsenTerjadwalLoading(false);
    }
  };
  const openAbsenTerjadwalKeteranganModal = () => {
    setAbsenTerjadwalKeteranganDraft(String(absenTerjadwalForm.keterangan || '').trim());
    setAbsenTerjadwalLampiranDraft(Array.isArray(absenTerjadwalAttachments) ? [...absenTerjadwalAttachments] : []);
    setAbsenTerjadwalInvalidCaptionIndexes([]);
    setShowAbsenTerjadwalKeteranganModal(true);
  };
  const closeAbsenTerjadwalKeteranganModal = () => {
    setShowAbsenTerjadwalKeteranganModal(false);
    setAbsenTerjadwalKeteranganDraft('');
    setAbsenTerjadwalLampiranDraft([]);
    setAbsenTerjadwalInvalidCaptionIndexes([]);
  };
  const saveAbsenTerjadwalKeteranganDraft = () => {
    const invalidIndexes = (Array.isArray(absenTerjadwalLampiranDraft) ? absenTerjadwalLampiranDraft : [])
      .map((item, idx) => (String(item?.caption || '').trim() ? -1 : idx))
      .filter((idx) => idx >= 0);
    if (invalidIndexes.length > 0) {
      setAbsenTerjadwalInvalidCaptionIndexes(invalidIndexes);
      showNotif('error', 'Caption dokumen wajib diisi untuk semua lampiran.');
      return;
    }
    setAbsenTerjadwalForm((prev) => ({
      ...prev,
      keterangan: String(absenTerjadwalKeteranganDraft || '').trim(),
    }));
    setAbsenTerjadwalAttachments(Array.isArray(absenTerjadwalLampiranDraft) ? [...absenTerjadwalLampiranDraft] : []);
    closeAbsenTerjadwalKeteranganModal();
  };
  const handleSubmitAbsenTerjadwal = async () => {
    const pegawai = selectedAbsenTerjadwalPegawai;
    const kategori = String(absenTerjadwalForm.kategori || '').trim().toUpperCase();
    const tanggalMulai = String(absenTerjadwalForm.tanggalMulai || '').trim();
    const tanggalSelesai = String(absenTerjadwalForm.tanggalSelesai || '').trim();
    if (!pegawai || !kategori || !tanggalMulai || !tanggalSelesai) {
      showNotif('error', 'Nama Pegawai, Kategori, Tanggal Mulai, dan Tanggal Selesai wajib diisi.');
      return;
    }
    if (tanggalSelesai < tanggalMulai) {
      showNotif('error', 'Tanggal Selesai tidak boleh lebih awal dari Tanggal Mulai.');
      return;
    }
    const invalidIndexes = collectAbsenTerjadwalInvalidCaptionIndexes();
    if (invalidIndexes.length > 0) {
      setAbsenTerjadwalInvalidCaptionIndexes(invalidIndexes);
      showNotif('error', 'Caption dokumen wajib diisi untuk semua lampiran.');
      return;
    }
    const formData = new FormData();
    const lampiranMeta = [];
    (Array.isArray(absenTerjadwalAttachments) ? absenTerjadwalAttachments : []).forEach((item, idx) => {
      if (!(item?.file instanceof File)) return;
      const uploadToken = `${String(pegawai.username || pegawai.key || 'pegawai').trim()}-${idx}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      formData.append('files', item.file);
      formData.append('file_tokens', uploadToken);
      lampiranMeta.push({ upload_token: uploadToken, caption: String(item?.caption || '').trim() });
    });
    formData.append('username', String(pegawai.username || pegawai.key || '').trim());
    formData.append('kategori', kategori);
    formData.append('tanggal_mulai', tanggalMulai);
    formData.append('tanggal_selesai', tanggalSelesai);
    formData.append('keterangan', String(absenTerjadwalForm.keterangan || '').trim());
    formData.append('lampiran_meta', JSON.stringify(lampiranMeta));

    try {
      setAbsenTerjadwalSubmitting(true);
      const { data } = await api.post(`${backendPort}/api/main/dashboard_absen_terjadwal_submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        requestGroup: REQ_GROUP_DASHBOARD_ABSEN_TERJADWAL_SUBMIT,
        timeout: 120000,
      });
      showNotif('success', data?.message || 'Daftar absen terjadwal berhasil disimpan.');
      setAbsenTerjadwalForm((prev) => ({
        ...prev,
        pegawaiKey: '',
        kategori: 'CUTI',
        keterangan: '',
      }));
      setAbsenTerjadwalAttachments([]);
      setAbsenTerjadwalInvalidCaptionIndexes([]);
      await loadAbsenTerjadwal();
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal submit dashboard_absen_terjadwal_submit:', err);
        showNotif('error', err?.response?.data?.message || 'Gagal menyimpan daftar absen terjadwal.');
      }
    } finally {
      setAbsenTerjadwalSubmitting(false);
    }
  };
  const openAbsenTerjadwalLampiran = (row) => {
    const files = (Array.isArray(row?.lampiran) ? row.lampiran : [])
      .map((item, idx) => {
        const fileObj = item?.file instanceof File ? item.file : null;
        const rawPath = String(item?.path || item?.url || '').trim();
        const rawFile = typeof item?.file === 'string' ? String(item.file).trim() : '';
        const url = item?.preview
          || (fileObj ? URL.createObjectURL(fileObj) : '')
          || (rawPath ? (rawPath.startsWith('http') ? rawPath : `${backendPort}${rawPath}`) : '')
          || (rawFile ? `${backendPort}/uploads/dokumen/${rawFile}` : '');
        if (!url) return null;
        return {
          url,
          name: String(item?.name || fileObj?.name || rawFile || `Lampiran ${idx + 1}`).trim(),
          caption: String(item?.caption || item?.name || `Lampiran ${idx + 1}`).trim(),
          note: `${String(row?.nama || '').trim()} - ${String(item?.caption || item?.name || `Lampiran ${idx + 1}`).trim()}`.trim(),
        };
      })
      .filter(Boolean);
    if (!files.length) return;
    setSelectedAbsenTerjadwalLampiranFiles(files);
    setShowAbsenTerjadwalLampiranModal(true);
  };
  const handleConfirmDeleteAbsenTerjadwal = async () => {
    const targetId = String(absenTerjadwalDeleteTarget?.id || '').trim();
    if (!targetId) {
      setAbsenTerjadwalDeleteTarget(null);
      return;
    }
    try {
      setAbsenTerjadwalDeleting(true);
      const { data } = await api.delete(`${backendPort}/api/main/dashboard_absen_terjadwal/${targetId}`, {
        requestGroup: REQ_GROUP_DASHBOARD_ABSEN_TERJADWAL_DELETE,
        timeout: 120000,
      });
      showNotif('success', data?.message || 'Daftar absen terjadwal berhasil dihapus.');
      setAbsenTerjadwalDeleteTarget(null);
      await loadAbsenTerjadwal();
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal hapus dashboard_absen_terjadwal:', err);
        showNotif('error', err?.response?.data?.message || 'Gagal menghapus daftar absen terjadwal.');
      }
    } finally {
      setAbsenTerjadwalDeleting(false);
    }
  };
  const handleTampilkanAbsenTerjadwal = () => {
    const awal = String(absenTerjadwalPeriode.tanggalAwal || '').trim();
    const akhir = String(absenTerjadwalPeriode.tanggalAkhir || '').trim();
    if (awal && akhir && akhir < awal) {
      showNotif('error', 'Tanggal Akhir tidak boleh lebih awal dari Tanggal Awal.');
      return;
    }
    loadAbsenTerjadwal({ tanggalAwal: awal, tanggalAkhir: akhir });
  };
  useEffect(() => {
    if (!showAbsenTerjadwalModal) return;
    const tanggalAwal = String(absenTerjadwalPeriode.tanggalAwal || getJakartaMonthStartKey()).trim();
    const tanggalAkhir = String(absenTerjadwalPeriode.tanggalAkhir || getJakartaDateKey()).trim();
    loadAbsenTerjadwal({ tanggalAwal, tanggalAkhir });
  }, [showAbsenTerjadwalModal, supervisiTargetCabangKode]);
  useEffect(() => {
    if (!showSupervisiAbsensiModal) return;
    loadSupervisiAbsensi(supervisiTanggal || getJakartaDateKey());
  }, [showSupervisiAbsensiModal, supervisiTanggal, supervisiTargetCabangKode]);
  const toggleSupervisiChecked = (rowKey, nextChecked) => {
    setSupervisiCheckedMap((prev) => ({ ...prev, [rowKey]: Boolean(nextChecked) }));
  };
  const handleSupervisiStatusChange = (rowKey, nextValue) => {
    const normalized = normalizeSupervisiStatus(nextValue);
    const targetRow = (Array.isArray(supervisiRows) ? supervisiRows : []).find((row) => String(row?.key || '').trim() === String(rowKey || '').trim()) || null;
    const hasValidJamAbsen = Boolean(String(targetRow?.jamAbsen || '').trim() && String(targetRow?.jamAbsen || '').trim() !== '-');
    const hasValidLokasi = Boolean(String(targetRow?.lokasi || '').trim() && String(targetRow?.lokasi || '').trim() !== '-');
    if (normalized === 'HADIR' && (!hasValidJamAbsen || !hasValidLokasi)) {
      showNotif('error', 'Status HADIR hanya boleh dipilih jika Jam Absen dan Lokasi sudah terisi.');
      return;
    }
    setSupervisiStatusMap((prev) => ({ ...prev, [rowKey]: normalized }));
    if (!normalized) {
      setSupervisiCheckedMap((prev) => ({ ...prev, [rowKey]: false }));
    }
  };
  const supervisiSelectedCount = useMemo(
    () => supervisiRows.filter((row) => Boolean(supervisiCheckedMap?.[row.key])).length,
    [supervisiRows, supervisiCheckedMap]
  );
  const supervisiSelectableKeys = useMemo(
    () => supervisiRows
      .filter((row) => Boolean(String(supervisiStatusMap?.[row.key] || '').trim()))
      .map((row) => row.key),
    [supervisiRows, supervisiStatusMap]
  );
  const supervisiAllChecked = useMemo(
    () => supervisiSelectableKeys.length > 0 && supervisiSelectableKeys.every((key) => Boolean(supervisiCheckedMap?.[key])),
    [supervisiSelectableKeys, supervisiCheckedMap]
  );
  const handleSubmitSupervisi = async () => {
    if (supervisiSelectedCount <= 0) {
      showNotif('info', 'Pilih minimal 1 baris yang telah disupervisi.');
      return;
    }
    const selectedRows = supervisiRows.filter((row) => Boolean(supervisiCheckedMap?.[row.key]));
    if (!selectedRows.length) {
      showNotif('info', 'Pilih minimal 1 baris yang telah disupervisi.');
      return;
    }

    const invalidStatusRow = selectedRows.find((row) => !String(supervisiStatusMap?.[row.key] || '').trim());
    if (invalidStatusRow) {
      showNotif('error', `Status absen untuk ${invalidStatusRow.nama} belum dipilih.`);
      return;
    }

    const invalidCaptionRow = selectedRows.find((row) => {
      const currentStatus = String(supervisiStatusMap?.[row.key] || '').trim().toUpperCase();
      if (!currentStatus || currentStatus === 'HADIR') return false;
      const attachments = Array.isArray(supervisiLampiranMap?.[row.key]?.attachments)
        ? supervisiLampiranMap[row.key].attachments
        : [];
      return attachments.some((item) => !String(item?.caption || '').trim());
    });
    if (invalidCaptionRow) {
      showNotif('error', `Caption dokumen untuk ${invalidCaptionRow.nama} wajib diisi.`);
      return;
    }

    const formData = new FormData();
    const entries = selectedRows.map((row) => {
      const currentStatus = String(supervisiStatusMap?.[row.key] || '').trim().toUpperCase();
      const draft = supervisiLampiranMap?.[row.key] || { attachments: [], keterangan: '' };
      const allowKeterangan = currentStatus && currentStatus !== 'HADIR';
      const attachments = Array.isArray(draft.attachments) ? draft.attachments : [];
      const existingLampiran = [];
      const newFiles = [];

      attachments.forEach((item, idx) => {
        const caption = String(item?.caption || '').trim();
        if (item?.file instanceof File) {
          const uploadToken = `${String(row.key || 'row').trim()}-${idx}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
          formData.append('files', item.file);
          formData.append('file_tokens', uploadToken);
          newFiles.push({ upload_token: uploadToken, caption });
          return;
        }
        const sanitized = sanitizeSupervisiLampiranItem(item);
        if (sanitized) {
          existingLampiran.push(sanitized);
        }
      });

      return {
        username: row.username || row.key,
        status_absen: currentStatus,
        keterangan: allowKeterangan ? String(draft.keterangan || '').trim() : '',
        existing_lampiran: allowKeterangan ? existingLampiran : [],
        new_files: allowKeterangan ? newFiles : [],
      };
    });

    formData.append('tanggal', String(supervisiTanggal || '').trim());
    formData.append('kode_cabang', String(supervisiTargetCabangKode || '').trim());
    formData.append('entries', JSON.stringify(entries));

    try {
      setSupervisiSubmitting(true);
      const { data } = await api.post(`${backendPort}/api/main/dashboard_supervisi_absensi_submit`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        requestGroup: REQ_GROUP_DASHBOARD_SUPERVISI_ABSENSI_SUBMIT,
        timeout: 120000,
      });
      showNotif('success', data?.message || 'Supervisi absensi berhasil disimpan.');
      await loadSupervisiAbsensi(supervisiTanggal);
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error('Gagal submit dashboard_supervisi_absensi_submit:', err);
        showNotif('error', err?.response?.data?.message || 'Gagal menyimpan supervisi absensi.');
      }
    } finally {
      setSupervisiSubmitting(false);
    }
  };
  const handleToggleAllSupervisi = (nextChecked) => {
    setSupervisiCheckedMap((prev) => {
      const next = { ...prev };
      supervisiRows.forEach((row) => {
        const canCheckRow = Boolean(String(supervisiStatusMap?.[row.key] || '').trim());
        if (!canCheckRow) {
          next[row.key] = false;
          return;
        }
        next[row.key] = Boolean(nextChecked);
      });
      return next;
    });
  };
  const openSupervisiKeteranganModal = (row) => {
    if (!row) return;
    const draft = supervisiLampiranMap?.[row.key] || { attachments: [], keterangan: '' };
    setSelectedSupervisiRow(row);
    setSupervisiLampiranDraft(Array.isArray(draft.attachments) ? [...draft.attachments] : []);
    setSupervisiKeteranganDraft(String(draft.keterangan || '').trim());
    setSupervisiInvalidCaptionIndexes([]);
    setShowSupervisiKeteranganModal(true);
  };
  const closeSupervisiKeteranganModal = () => {
    setShowSupervisiKeteranganModal(false);
    setSelectedSupervisiRow(null);
    setSupervisiLampiranDraft([]);
    setSupervisiKeteranganDraft('');
    setSupervisiInvalidCaptionIndexes([]);
  };
  const saveSupervisiKeteranganDraft = () => {
    const invalidIndexes = (Array.isArray(supervisiLampiranDraft) ? supervisiLampiranDraft : [])
      .map((item, idx) => (String(item?.caption || '').trim() ? -1 : idx))
      .filter((idx) => idx >= 0);
    if (invalidIndexes.length > 0) {
      setSupervisiInvalidCaptionIndexes(invalidIndexes);
      showNotif('error', 'Caption dokumen wajib diisi untuk semua lampiran.');
      return;
    }
    const rowKey = String(selectedSupervisiRow?.key || '').trim();
    if (!rowKey) {
      closeSupervisiKeteranganModal();
      return;
    }
    setSupervisiLampiranMap((prev) => ({
      ...prev,
      [rowKey]: {
        attachments: Array.isArray(supervisiLampiranDraft) ? [...supervisiLampiranDraft] : [],
        keterangan: String(supervisiKeteranganDraft || '').trim(),
      },
    }));
    closeSupervisiKeteranganModal();
  };
  const buildDaftarHadirFotoUrl = (fotoRaw) => {
    const foto = String(fotoRaw || '').trim();
    if (!foto || foto === '-') return '';
    if (foto.startsWith('http://') || foto.startsWith('https://')) return foto;
    return `${backendPort}${foto.startsWith('/') ? foto : `/${foto}`}`;
  };
  const openDaftarHadirFotoModal = (row) => {
    const fotoUrl = buildDaftarHadirFotoUrl(row?.foto);
    if (!fotoUrl) return;
    setSelectedDaftarHadirFotoFiles([
      {
        url: fotoUrl,
        caption: row?.nama ? `Foto absensi - ${row.nama}` : 'Foto absensi',
        name: row?.nama || 'Foto absensi',
      },
    ]);
    setShowDaftarHadirFotoModal(true);
  };
  const filterMonitoringRowsByCabang = (kondisi, rows) => {
    const list = Array.isArray(rows) ? rows : [];
    if (!isPusatUser) return list;
    const selected = String(selectedMonitoringCabang || 'C-1').trim();
    if (!selected || selected === 'C-1') return list;
    if (String(kondisi || '').trim() === 'Ban di pabrik lebih dari 3 bulan') return list;
    return list.filter((row) => {
      const kode = String(
        row?.kode_cabang
        ?? row?.cabang
        ?? row?.kode_cabang_asal
        ?? row?.cabang_asal
        ?? ''
      ).trim().toUpperCase();
      return kode === selected.toUpperCase();
    });
  };
  const monitoringBanRows = useMemo(
    () => {
      const isPusat = actorKodeCabang === 'C-1';
      const selected = String(selectedMonitoringCabang || (isPusat ? 'C-1' : actorKodeCabang)).trim();
      const tables = monitoringPreloadPayload?.tables && typeof monitoringPreloadPayload.tables === 'object'
        ? monitoringPreloadPayload.tables
        : {};
      const selectedCabangLabel = isPusat
        ? (
          selected === 'C-1'
            ? 'Semua Cabang'
            : (mapNamaCabang.get(selected) || selected || '-')
        )
        : normalizedCabangName;
      return MONITORING_BAN_CONDITIONS
        .filter((kondisi) => {
          if (!isPusat) return kondisi !== 'Ban di pabrik lebih dari 3 bulan';
          if (kondisi === 'Ban di pabrik lebih dari 3 bulan' && selected !== 'C-1') return false;
          return true;
        })
        .map((kondisi) => {
          const rawRows = Array.isArray(tables?.[kondisi]) ? tables[kondisi] : null;
          const filteredRows = rawRows ? filterMonitoringRowsByCabang(kondisi, rawRows) : null;
          const fallbackCount = monitoringCounts[kondisi];
          const jumlah = filteredRows ? filteredRows.length : (fallbackCount ?? 'nodata');
          return {
            kondisi,
            cabang: kondisi === 'Ban di pabrik lebih dari 3 bulan' ? 'Semua Suplier' : selectedCabangLabel,
            jumlah,
          };
        })
        .filter((row) => {
          if (row.kondisi !== MONITORING_MT_NONAKTIF_CONDITION) return true;
          const jumlahNum = Number(String(row.jumlah).replace(/\./g, ''));
          return Number.isFinite(jumlahNum) && jumlahNum > 0;
        });
    },
    [actorKodeCabang, monitoringCounts, monitoringPreloadPayload, normalizedCabangName, isPusatUser, selectedMonitoringCabang, mapNamaCabang]
  );
  const trendMaxOffset = useMemo(() => {
    const sorted = Array.isArray(trendRows) ? trendRows : [];
    const total = sorted.length;
    const windowSize = Math.max(10, Math.min(100, Number(trendWindowDays) || 30));
    return Math.max(0, total - windowSize);
  }, [trendRows, trendWindowDays]);
  const visibleTrendRows = useMemo(() => {
    const sorted = Array.isArray(trendRows) ? trendRows : [];
    const windowSize = Math.max(10, Math.min(100, Number(trendWindowDays) || 30));
    const offset = Math.max(0, Math.min(trendMaxOffset, Number(trendWindowOffset) || 0));
    return sorted.slice(offset, offset + windowSize);
  }, [trendRows, trendWindowDays, trendWindowOffset, trendMaxOffset]);
  useEffect(() => {
    setTrendWindowOffset((prev) => Math.max(0, Math.min(trendMaxOffset, Number(prev) || 0)));
  }, [trendMaxOffset]);
  const trendAxisBounds = useMemo(() => {
    const ordered = [...visibleTrendRows].reverse();
    const jarakValues = ordered.map((row) => Number(row?.jarak_tempuh_harian || 0)).filter((v) => Number.isFinite(v));
    const ausValues = ordered.map((row) => Number(row?.aus_harian || 0)).filter((v) => Number.isFinite(v));
    const getBounds = (values) => {
      if (!values.length) return { min: 0, max: 1, range: 1 };
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = Math.max(1, max - min);
      return { min, max, range };
    };
    const jarak = getBounds(jarakValues);
    const aus = getBounds(ausValues);
    return {
      jarakMinAxis: jarak.min - (jarak.range / 3),
      jarakMaxAxis: jarak.max,
      ausMinAxis: aus.min - (aus.range / 9),
      ausMaxAxis: aus.max + (aus.range / 3),
    };
  }, [visibleTrendRows]);
  const trendChartData = useMemo(() => {
    const ordered = [...visibleTrendRows].reverse();
    const labels = ordered.map((_row, idx) => `H-${Math.max(0, ordered.length - 1 - idx)}`);
    return {
      labels,
      datasets: [
        {
          label: 'KM Tempuh Harian',
          data: ordered.map((row) => Number(row?.jarak_tempuh_harian || 0)),
          metaTgl: ordered.map((row) => String(row?.tanggal || '')),
          yAxisID: 'yJarak',
          borderColor: palette.brand,
          backgroundColor: 'rgba(11,60,93,0.18)',
          fill: {
            target: { value: trendAxisBounds.jarakMinAxis },
            above: 'rgba(11,60,93,0.18)',
          },
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 6,
          pointHitRadius: 14,
        },
        {
          label: 'AUS Harian',
          data: ordered.map((row) => Number(row?.aus_harian || 0)),
          metaTgl: ordered.map((row) => String(row?.tanggal || '')),
          yAxisID: 'yAus',
          borderColor: palette.cyan,
          backgroundColor: 'rgba(14,165,164,0.20)',
          fill: {
            target: { value: trendAxisBounds.ausMinAxis },
            above: 'rgba(14,165,164,0.20)',
          },
          tension: 0.3,
          pointRadius: 2,
          pointHoverRadius: 6,
          pointHitRadius: 14,
        },
      ],
    };
  }, [trendAxisBounds.jarakMinAxis, trendAxisBounds.ausMinAxis, visibleTrendRows]);
  const trendChartOptions = useMemo(() => ({
    ...baseOptions,
    plugins: {
      ...baseOptions.plugins,
      tooltip: {
        ...baseOptions.plugins.tooltip,
        displayColors: false,
        callbacks: {
          title: (items) => {
            if (!Array.isArray(items) || !items.length) return '';
            const item = items[0];
            const ds = item?.dataset;
            const idx = Number(item?.dataIndex || 0);
            const dateText = Array.isArray(ds?.metaTgl) ? ds.metaTgl[idx] : '';
            return dateText || '-';
          },
          label: (context) => {
            const value = Number(context?.parsed?.y || 0);
            const isJarak = Number(context?.datasetIndex) === 0;
            if (isJarak) {
              const fmtJarak = Number.isFinite(value) ? value.toLocaleString('id-ID') : '0';
              return `Jarak: ${fmtJarak} km`;
            }
            const fmtAus = Number.isFinite(value)
              ? value.toLocaleString('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
              : '0';
            return `Aus: ${fmtAus} mm`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { display: false },
        grid: { display: false },
      },
      yJarak: {
        type: 'linear',
        position: 'left',
        min: trendAxisBounds.jarakMinAxis,
        max: trendAxisBounds.jarakMaxAxis,
        ticks: { display: false },
        grid: { display: false },
        border: { display: false },
      },
      yAus: {
        type: 'linear',
        position: 'right',
        min: trendAxisBounds.ausMinAxis,
        max: trendAxisBounds.ausMaxAxis,
        ticks: { display: false },
        grid: { display: false },
        border: { display: false },
      },
    },
  }), [trendAxisBounds]);
  const notifBellColor = pendingAlertsTotal > 0 ? '#ff8001' : '#9CA3AF';
  const goToPendingTarget = (row) => {
    const target = row?.target && typeof row.target === 'object' ? row.target : null;
    if (!target?.page) return;
    if (typeof window !== 'undefined') {
      window[DASHBOARD_NAV_INTENT_KEY] = {
        key: String(row?.key || '').trim(),
        page: String(target.page || '').trim(),
        tab: String(target.tab || '').trim(),
        action: String(target.action || '').trim(),
        at: Date.now(),
      };
    }
    setIsNotifOpen(false);
    setPage?.(String(target.page || 'Page23'));
  };
  const mapNopolByIdMt = useMemo(() => {
    const m = new Map();
    const list = Array.isArray(globalOpsi?.opsiMT) ? globalOpsi.opsiMT : [];
    for (const row of list) {
      const idMt = String(row?.id_mt || '').trim().toUpperCase();
      const nopol = String(row?.nopol || '').trim();
      if (!idMt) continue;
      m.set(idMt, nopol || idMt);
    }
    return m;
  }, [globalOpsi?.opsiMT]);
  const mapSeriByIdBan = useMemo(() => {
    const m = new Map();
    const list = Array.isArray(globalOpsi?.opsiBan) ? globalOpsi.opsiBan : [];
    for (const row of list) {
      const idBan = Number(row?.id_ban || 0);
      const seri = String(row?.seri_ban || '').trim();
      if (!Number.isFinite(idBan) || idBan <= 0) continue;
      m.set(String(idBan), seri || String(idBan));
    }
    return m;
  }, [globalOpsi?.opsiBan]);
  const normalizeMonitoringRowsForShowData = (rows, kondisi = '') => {
    const formatYYYYMMDD = (date) => {
      if (!date) return null;
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return null;
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    const isDateKey = (key) => {
      const k = String(key || '').toLowerCase();
      return k.includes('tgl') || k.includes('tanggal') || k.includes('date') || k.endsWith('_at') || k.endsWith('at');
    };
    const isIsoDateTimeString = (value) => {
      const s = String(value || '').trim();
      return /^\d{4}-\d{2}-\d{2}t\d{2}:\d{2}:\d{2}(\.\d+)?z$/i.test(s);
    };
    const list = Array.isArray(rows) ? rows : [];
    const normalized = list.map((src) => {
      const row = src && typeof src === 'object' ? { ...src } : {};
      for (const key of Object.keys(row)) {
        const shouldNormalize = isDateKey(key) || isIsoDateTimeString(row[key]);
        if (!shouldNormalize) continue;
        const formatted = formatYYYYMMDD(row[key]);
        row[key] = formatted || row[key];
      }
      if (Object.prototype.hasOwnProperty.call(row, 'id_ban')) {
        const idBanKey = String(row?.id_ban ?? '').trim();
        row.seri_ban = mapSeriByIdBan.get(idBanKey) || idBanKey || '-';
      }
      if (Object.prototype.hasOwnProperty.call(row, 'kode_cabang')) {
        const kode = String(row?.kode_cabang ?? '').trim();
        row.cabang = mapNamaCabang.get(kode) || kode || '-';
        delete row.kode_cabang;
      }
      if (Object.prototype.hasOwnProperty.call(row, 'id_mt')) {
        const idMtKey = String(row?.id_mt ?? '').trim().toUpperCase();
        row.nopol = mapNopolByIdMt.get(idMtKey) || idMtKey || '-';
      }
      return row;
    });
    if (String(kondisi || '').trim() === 'MT odo belum update >= 2 hari') {
      return normalized.map((row) => {
        const cabangRaw = String(row?.cabang ?? row?.kode_cabang ?? '').trim();
        const idMtRaw = String(row?.id_mt ?? row?.nopol ?? '').trim().toUpperCase();
        const lastCheckRaw = row?.last_check ?? row?.last_odo_date ?? row?.tanggal_terakhir ?? row?.tgl_terakhir ?? row?.tgl ?? row?.tanggal ?? '';
        const lastOdoRaw = row?.last_odo ?? row?.odo_terakhir ?? row?.odo ?? row?.kmt_terakhir ?? row?.kmt ?? '';
        const durasiRaw = row?.durasi ?? row?.selisih_hari ?? row?.hari ?? row?.umur_hari ?? '';
        const lastCheck = formatYYYYMMDD(lastCheckRaw) || String(lastCheckRaw || '').trim() || '-';
        const lastOdo = String(lastOdoRaw ?? '').trim() || '-';
        const durasiHari = String(durasiRaw ?? '').trim() || '-';
        return {
          id_mt: idMtRaw || '-',
          cabang: mapNamaCabang.get(cabangRaw) || cabangRaw || '-',
          nopol: mapNopolByIdMt.get(idMtRaw) || idMtRaw || '-',
          last_check: lastCheck,
          last_odo: lastOdo,
          durasi_hari: durasiHari,
        };
      });
    }
    if (String(kondisi || '').trim() === 'MT dengan status Non Aktif') {
      return normalized.map((row) => {
        const cabangRaw = String(row?.cabang ?? row?.kode_cabang ?? '').trim();
        const idMtRaw = String(row?.id_mt ?? row?.nopol ?? '').trim().toUpperCase();
        const nopolRaw = String(
          row?.nopol
          ?? row?.no_pol
          ?? row?.nomor_polisi
          ?? mapNopolByIdMt.get(idMtRaw)
          ?? ''
        ).trim();
        const statusRaw = String(
          row?.status
          ?? row?.status_mt
          ?? row?.status_terakhir
          ?? 'NonAktif'
        ).trim();
        const tanggalRaw = row?.tgl_non_aktif ?? row?.tanggal_nonaktif ?? row?.tgl_nonaktif ?? row?.tanggal ?? row?.tgl ?? row?.updated_at ?? '';
        const lamaNonAktifRaw = row?.lama_non_aktif ?? row?.lama_nonaktif ?? row?.durasi_nonaktif ?? row?.durasi_non_aktif ?? row?.durasi ?? '';
        const catatanRaw = String(row?.catatan ?? row?.keterangan ?? row?.info ?? '').trim();
        const tanggalNonaktif = formatYYYYMMDD(tanggalRaw) || String(tanggalRaw || '').trim() || '-';
        const lamaNonAktif = String(lamaNonAktifRaw ?? '').trim() || '-';
        return {
          id_mt: idMtRaw || '-',
          cabang: mapNamaCabang.get(cabangRaw) || cabangRaw || '-',
          nopol: nopolRaw || mapNopolByIdMt.get(idMtRaw) || idMtRaw || '-',
          status: statusRaw || 'NonAktif',
          tgl_non_aktif: tanggalNonaktif,
          lama_non_aktif: lamaNonAktif,
          ...(catatanRaw ? { catatan: catatanRaw } : {}),
        };
      });
    }
    if (String(kondisi || '').trim() === 'Ban Aus Tidak Merata') {
      return normalized.map((row) => {
        const cabangRaw = String(row?.cabang ?? row?.kode_cabang ?? '').trim();
        const idMtRaw = String(row?.id_mt ?? row?.nopol ?? '').trim().toUpperCase();
        const posisi = String(row?.posisi ?? row?.position ?? '-').trim() || '-';
        const seriBan = String(row?.seri_ban ?? row?.seri ?? row?.ban ?? '-').trim() || '-';
        const status = String(row?.status ?? row?.status_ban ?? '-').trim() || '-';
        const tglPasangRaw = row?.tgl_pasang ?? row?.tanggal_pasang ?? row?.tgl_pasang_awal ?? '';
        const td = String(row?.td ?? row?.td_laporan ?? row?.td_min ?? '-').trim() || '-';
        const selisihTd = String(row?.selisih_td ?? row?.delta_td ?? row?.selisih ?? '-').trim() || '-';
        const tglPasang = formatYYYYMMDD(tglPasangRaw) || String(tglPasangRaw || '').trim() || '-';
        return {
          id_mt: idMtRaw || '-',
          id_ban: String(row?.id_ban ?? '').trim() || '-',
          cabang: mapNamaCabang.get(cabangRaw) || cabangRaw || '-',
          nopol: mapNopolByIdMt.get(idMtRaw) || idMtRaw || '-',
          posisi,
          seri_ban: seriBan,
          status,
          tgl_pasang: tglPasang,
          td,
          selisih_td: selisihTd,
        };
      });
    }
    if (String(kondisi || '').trim() === 'Pasangan Ban dengan TD Tidak Seimbang') {
      return normalized.map((row) => {
        const cabangRaw = String(row?.cabang ?? row?.kode_cabang ?? '').trim();
        const idMtRaw = String(row?.id_mt ?? row?.nopol ?? '').trim().toUpperCase();
        const posisi1 = String(row?.posisi1 ?? row?.posisi_1 ?? row?.position1 ?? '-').trim() || '-';
        const td1 = String(row?.td1 ?? row?.td_1 ?? row?.tdmin1 ?? row?.td_min_1 ?? '-').trim() || '-';
        const posisi2 = String(row?.posisi2 ?? row?.posisi_2 ?? row?.position2 ?? '-').trim() || '-';
        const td2 = String(row?.td2 ?? row?.td_2 ?? row?.tdmin2 ?? row?.td_min_2 ?? '-').trim() || '-';
        const selisihTdmin = String(
          row?.selisih_tdmin ?? row?.selisih_td ?? row?.delta_tdmin ?? row?.selisih ?? '-'
        ).trim() || '-';
        return {
          id_mt: idMtRaw || '-',
          cabang: mapNamaCabang.get(cabangRaw) || cabangRaw || '-',
          nopol: mapNopolByIdMt.get(idMtRaw) || idMtRaw || '-',
          posisi1,
          td1,
          posisi2,
          td2,
          selisih_tdmin: selisihTdmin,
        };
      });
    }
    if (String(kondisi || '').trim() === 'TD Stagnan setelah berjalan > 10.000 KM') {
      return normalized.map((row) => {
        const cabangRaw = String(row?.cabang ?? row?.kode_cabang ?? '').trim();
        const idMtRaw = String(row?.id_mt ?? row?.nopol ?? '').trim().toUpperCase();
        const posisi = String(row?.posisi ?? row?.position ?? '-').trim() || '-';
        const seriBan = String(row?.seri_ban ?? row?.seri ?? row?.ban ?? '-').trim() || '-';
        const td = String(row?.td ?? row?.td_laporan ?? row?.td_min ?? '-').trim() || '-';
        const kmtSekarang = String(row?.kmt_sekarang ?? row?.kmt_now ?? row?.kmt ?? row?.kmt_akhir ?? '-').trim() || '-';
        const kmtAwalTd = String(row?.kmt_awal_td ?? row?.kmt_awal ?? row?.kmt_start ?? '-').trim() || '-';
        const jarakTempuh = String(row?.jarak_tempuh ?? row?.delta_kmt ?? row?.selisih_kmt ?? '-').trim() || '-';
        const tanggalAwalTdRaw = row?.tanggal_awal_td ?? row?.tgl_awal_td ?? row?.tgl_awal ?? row?.tanggal_awal ?? '';
        const durasiHari = String(row?.durasi_hari ?? row?.durasi ?? row?.selisih_hari ?? row?.hari ?? '-').trim() || '-';
        const tanggalAwalTd = formatYYYYMMDD(tanggalAwalTdRaw) || String(tanggalAwalTdRaw || '').trim() || '-';
        return {
          id_mt: idMtRaw || '-',
          id_ban: String(row?.id_ban ?? '').trim() || '-',
          cabang: mapNamaCabang.get(cabangRaw) || cabangRaw || '-',
          nopol: mapNopolByIdMt.get(idMtRaw) || idMtRaw || '-',
          posisi,
          seri_ban: seriBan,
          td,
          kmt_sekarang: kmtSekarang,
          kmt_awal_td: kmtAwalTd,
          jarak_tempuh: jarakTempuh,
          tanggal_awal_td: tanggalAwalTd,
          durasi_hari: durasiHari,
        };
      });
    }
    if (String(kondisi || '').trim() === 'Ban dengan TDmin <= 3 mm') {
      return normalized.map((row) => {
        const cabangRaw = String(row?.cabang ?? row?.kode_cabang ?? '').trim();
        const idMtRaw = String(row?.id_mt ?? row?.nopol ?? '').trim().toUpperCase();
        const posisi = String(row?.posisi ?? row?.position ?? '-').trim() || '-';
        const seriBan = String(row?.seri_ban ?? row?.seri ?? row?.ban ?? '-').trim() || '-';
        const tdSekarang = String(row?.td_sekarang ?? row?.td_now ?? row?.td ?? row?.td_laporan ?? '-').trim() || '-';
        const tdMin = String(row?.tdmin_sekarang ?? row?.td_min ?? row?.tdmin ?? row?.minimum_td ?? '-').trim() || '-';
        const ausBanSejakPasang = String(
          row?.aus_ban ?? row?.aus_ban_sejak_pasang ?? row?.aus_sejak_pasang ?? row?.delta_td_sejak_pasang ?? row?.selisih_td_sejak_pasang ?? '-'
        ).trim() || '-';
        const jarakTempuhSejakPasang = String(
          row?.jarak_tempuh_sejak_pasang ?? row?.kmt_sejak_pasang ?? row?.delta_kmt_sejak_pasang ?? row?.jarak_tempuh ?? '-'
        ).trim() || '-';
        const tglPasangRaw = row?.tgl_pasang ?? row?.tanggal_pasang ?? row?.tgl_pasang_awal ?? '';
        const durasiHari = String(row?.durasi_hari ?? row?.durasi ?? row?.selisih_hari ?? row?.hari ?? '-').trim() || '-';
        const tglPasang = formatYYYYMMDD(tglPasangRaw) || String(tglPasangRaw || '').trim() || '-';
        return {
          id_mt: idMtRaw || '-',
          id_ban: String(row?.id_ban ?? '').trim() || '-',
          cabang: mapNamaCabang.get(cabangRaw) || cabangRaw || '-',
          nopol: mapNopolByIdMt.get(idMtRaw) || idMtRaw || '-',
          posisi,
          seri_ban: seriBan,
          td_sekarang: tdSekarang,
          td_min: tdMin,
          aus_ban_sejak_pasang: ausBanSejakPasang,
          jarak_tempuh_sejak_pasang: jarakTempuhSejakPasang,
          tgl_pasang: tglPasang,
          durasi_hari: durasiHari,
        };
      });
    }
    if (String(kondisi || '').trim() === 'Ban di Gudang lebih dari 3 bulan') {
      return normalized.map((row) => {
        const cabangRaw = String(row?.cabang ?? row?.kode_cabang ?? '').trim();
        const seriBan = String(row?.seri_ban ?? row?.seri ?? row?.ban ?? '-').trim() || '-';
        const status = String(row?.status ?? row?.status_ban ?? '-').trim() || '-';
        const tanggalMasukGudangRaw = row?.tanggal_masuk_gudang ?? row?.tgl_masuk_gudang ?? row?.tgl_masuk ?? row?.tanggal_masuk ?? '';
        const durasiHari = String(row?.durasi_hari ?? row?.durasi ?? row?.selisih_hari ?? row?.hari ?? '-').trim() || '-';
        const tanggalMasukGudang = formatYYYYMMDD(tanggalMasukGudangRaw) || String(tanggalMasukGudangRaw || '').trim() || '-';
        return {
          id_ban: String(row?.id_ban ?? '').trim() || '-',
          cabang: mapNamaCabang.get(cabangRaw) || cabangRaw || '-',
          seri_ban: seriBan,
          status,
          tanggal_masuk_gudang: tanggalMasukGudang,
          durasi_hari: durasiHari,
        };
      });
    }
    if (String(kondisi || '').trim() === 'Ban di pabrik lebih dari 3 bulan') {
      return normalized.map((row) => {
        const suplier = String(row?.suplier ?? row?.supplier ?? row?.vendor ?? '-').trim() || '-';
        const seriBan = String(row?.seri_ban ?? row?.seri ?? row?.ban ?? '-').trim() || '-';
        const status = String(row?.status ?? row?.status_ban ?? '-').trim() || '-';
        const cabangAsalRaw = String(row?.cabang_asal ?? row?.kode_cabang_asal ?? row?.cabang ?? row?.kode_cabang ?? '').trim();
        const tanggalMasukPabrikRaw = row?.tanggal_masuk_pabrik ?? row?.tgl_masuk_pabrik ?? row?.tgl_masuk ?? row?.tanggal_masuk ?? '';
        const durasiHari = String(row?.durasi_hari ?? row?.durasi ?? row?.selisih_hari ?? row?.hari ?? '-').trim() || '-';
        const tanggalMasukPabrik = formatYYYYMMDD(tanggalMasukPabrikRaw) || String(tanggalMasukPabrikRaw || '').trim() || '-';
        return {
          id_ban: String(row?.id_ban ?? '').trim() || '-',
          suplier,
          seri_ban: seriBan,
          status,
          cabang_asal: mapNamaCabang.get(cabangAsalRaw) || cabangAsalRaw || '-',
          tanggal_masuk_pabrik: tanggalMasukPabrik,
          durasi_hari: durasiHari,
        };
      });
    }
    return normalized;
  };
  const openMonitoringDetailModal = (kondisi) => {
    const key = String(kondisi || '').trim();
    const rawTables = monitoringPreloadPayload?.tables && typeof monitoringPreloadPayload.tables === 'object'
      ? monitoringPreloadPayload.tables
      : {};
    const rowsRaw = Array.isArray(rawTables?.[key]) ? rawTables[key] : [];
    const filteredRows = filterMonitoringRowsByCabang(key, rowsRaw);
    const rows = normalizeMonitoringRowsForShowData(filteredRows, key);
    if (typeof window !== 'undefined') {
      const prev = (window.__laporan_runtime_state && typeof window.__laporan_runtime_state === 'object')
        ? window.__laporan_runtime_state
        : {};
      window.__laporan_runtime_state = {
        ...prev,
        viewMeta: {
          title: `MONITORING BAN - ${key}`,
          tanggal: new Date().toLocaleDateString('id-ID'),
          report_key: `MONITORING_BAN|${key}`,
        },
        cacheMap: {
          ...(prev?.cacheMap && typeof prev.cacheMap === 'object' ? prev.cacheMap : {}),
          [`MONITORING_BAN|${key}`]: { rows },
        },
      };
    }
    setShowMonitoringDetailModal(true);
  };
  const closeMonitoringDetailToDashboard = () => {
    setShowMonitoringDetailModal(false);
    setPage?.('Dashboard');
  };
  const normalizeLastTrxInfo = (rawText) => {
    const raw = String(rawText || '').trim();
    if (!raw) return '-';
    let text = raw;
    text = text.replace(/(Dokumen\s*:\s*)([^|,\n\r]+)/gi, (_m, p1, p2) => {
      const value = String(p2 || '').trim();
      const [nomor, tanggal] = value.split('#');
      if (nomor && tanggal) return `${p1}${String(nomor).trim()} tgl. ${String(tanggal).trim()}`;
      return `${p1}${value}`;
    });
    text = text.replace(/\bC-[A-Z0-9-]+\b/gi, (token) => {
      const key = String(token || '').trim().toUpperCase();
      return mapNamaCabang.get(key) || token;
    });
    text = text.replace(/\bK-[A-Z0-9-]+\b/gi, (token) => {
      const key = String(token || '').trim().toUpperCase();
      return mapNopolByIdMt.get(key) || token;
    });
    text = text.replace(/\bid_ban\s*:\s*(\d+)\b/gi, (_m, idBan) => {
      const mapped = mapSeriByIdBan.get(String(idBan || '').trim()) || String(idBan || '').trim();
      return `id_ban: ${mapped}`;
    });
    return text || '-';
  };
  const openLastBanTrxModal = async () => {
    setShowLastBanTrxModal(true);
    setLastBanTrxLoading(true);
    setLastBanTrxError('');
    try {
      const { data } = await api.get(`${backendPort}/api/main/dashboard_ban_last_trx`, { timeout: 20000 });
      setLastBanTrxRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      setLastBanTrxRows([]);
      setLastBanTrxError(err?.response?.data?.message || 'Gagal memuat 30 transaksi terakhir');
    } finally {
      setLastBanTrxLoading(false);
    }
  };

  const miniSnapshotForSelectedCabang = useMemo(() => {
    if (!isPusatUser) return miniSnapshot;
    const selected = String(selectedKomposisiCabang || 'C-1').trim();
    const rows = Array.isArray(miniRows) ? miniRows : [];
    const pickByKode = (kode) => rows.find((x) => String(x?.kode_cabang || '').trim().toUpperCase() === String(kode || '').trim().toUpperCase()) || null;
    if (selected === 'C-1') {
      return pickByKode('TOTAL') || pickByKode('C-1') || miniSnapshot;
    }
    return pickByKode(selected) || {};
  }, [isPusatUser, selectedKomposisiCabang, miniRows, miniSnapshot]);

  const computedMini = useMemo(() => {
    const src = miniSnapshotForSelectedCabang && typeof miniSnapshotForSelectedCabang === 'object' ? miniSnapshotForSelectedCabang : {};
    const mtNow = num(src?.mt_now);
    const mtLast = num(src?.mt_last);
    const rodaNow = num(src?.roda_now);
    const kmtNow = num(src?.kmt_now);
    const kmtLast = num(src?.kmt_last);
    const banOtfNow = num(src?.ban_otf_now);
    const gudangNow = num(src?.gudang_now);
    const afkirNow = num(src?.afkir_now);
    const pabrikNow = num(src?.pabrik_now);
    const gOri = num(src?.g_ori);
    const gRetread = num(src?.g_retread);
    const gCasing = num(src?.g_casing);
    const gAfkir = num(src?.g_afkir);
    const pCasing = num(src?.p_casing);
    const oriNow = num(src?.ori_now);
    const retreadNow = num(src?.retread_now);
    const otherNow = num(src?.otf_other);
    const yearDays = Math.max(1, num(miniContext?.days_in_year) || 365);
    const yearDay = Math.min(yearDays, Math.max(1, num(miniContext?.day_of_year) || 1));
    const kmtBaselineProp = (kmtLast * yearDay) / yearDays;
    const inventoryNow = gOri + gRetread + gCasing + gAfkir;
    const gudangTotal = gOri + gRetread + gCasing + gAfkir;
    const tireUnderMgmt = actorKodeCabang === 'C-1' ? 
                            banOtfNow + gOri + gRetread + gCasing + gAfkir + pCasing
                            : banOtfNow + gOri + gRetread + gCasing + gAfkir
                            ;
    const otfAktif = rodaNow;
    const otfSpare = Math.max(0, banOtfNow - rodaNow);
    const gudangSpare = Math.max(0, gudangNow - afkirNow);
    return {
      mtNow, mtLast, rodaNow, kmtNow, kmtLast, banOtfNow, gudangNow, afkirNow, pabrikNow, gOri, gRetread, gCasing, gAfkir, pCasing, oriNow, retreadNow, otherNow,
      kmtBaselineProp, inventoryNow, tireUnderMgmt, gudangTotal, otfAktif, otfSpare, gudangSpare,
    };
  }, [miniSnapshotForSelectedCabang, miniContext, actorKodeCabang]);

  const miniSnapshotForKomposisi = useMemo(() => {
    return miniSnapshotForSelectedCabang;
  }, [miniSnapshotForSelectedCabang]);

  const computedKomposisi = useMemo(() => {
    const src = miniSnapshotForKomposisi && typeof miniSnapshotForKomposisi === 'object' ? miniSnapshotForKomposisi : {};
    const banOtfNow = num(src?.ban_otf_now);
    const gOri = num(src?.g_ori);
    const gRetread = num(src?.g_retread);
    const gCasing = num(src?.g_casing);
    const gAfkir = num(src?.g_afkir);
    const pCasingRaw = num(src?.p_casing);
    const oriNow = num(src?.ori_now);
    const retreadNow = num(src?.retread_now);
    const otherNow = num(src?.otf_other);
    const includePabrik = isPusatUser && String(selectedKomposisiCabang || 'C-1').trim() === 'C-1';
    const pCasing = includePabrik ? pCasingRaw : 0;
    const gudangTotal = gOri + gRetread + gCasing + gAfkir;
    const tireUnderMgmt = banOtfNow + gOri + gRetread + gCasing + gAfkir + pCasing;
    return {
      banOtfNow,
      gOri,
      gRetread,
      gCasing,
      gAfkir,
      pCasing,
      oriNow,
      retreadNow,
      otherNow,
      gudangTotal,
      tireUnderMgmt,
      includePabrik,
    };
  }, [miniSnapshotForKomposisi, isPusatUser, selectedKomposisiCabang]);

  const komposisiBanData = useMemo(() => ({
    labels: ['OTF ORI', 'OTF RETREAD', 'OTF OTHER', 'GUDANG ORI', 'GUDANG RETREAD', 'GUDANG CASING', 'AFKIR', computedKomposisi.includePabrik ? 'CASING DI PABRIK' : ''],
    datasets: [
      {
        data: [
          computedKomposisi.oriNow,
          computedKomposisi.retreadNow,
          computedKomposisi.otherNow,
          computedKomposisi.gOri,
          computedKomposisi.gRetread,
          computedKomposisi.gCasing,
          computedKomposisi.gAfkir,
          computedKomposisi.includePabrik ? computedKomposisi.pCasing : 0,
        ],
        backgroundColor: ['#0B3C5D', '#2563EB', '#0EA5A4', '#166c00', '#14B8A6', '#63ff02', '#fb1100', '#D97706'],
        borderWidth: 0,
      },
    ],
  }), [computedKomposisi]);

  const getBelanjaProgressWidth = (item) => {
    const label = String(item?.key || '').trim();
    const numerator = Number(item?.value || 0);
    const denominator = label === 'Pengadaan Ban'
      ? Number(computedKomposisi.oriNow || 0)
      : (label === 'Retread' ? Number(computedKomposisi.retreadNow || 0) : 0);
    if (denominator <= 0 || numerator <= 0) return 0;
    return Math.min(100, (numerator / denominator) * 100);
  };
  const getBelanjaProgressPercentText = (item) => `${Math.round(getBelanjaProgressWidth(item))}%`;

  const summaryCards = useMemo(() => {
    const mtDelta = computedMini.mtNow - computedMini.mtLast;
    const mtDirection = mtDelta >= 0 ? 'Naik' : 'Turun';
    const mtPct = deltaPct(computedMini.mtNow, computedMini.mtLast);
    const kmDelta = computedMini.kmtNow - computedMini.kmtBaselineProp;
    const kmDirection = kmDelta >= 0 ? 'Naik' : 'Turun';
    const kmPct = deltaPct(computedMini.kmtNow, computedMini.kmtBaselineProp);
    const kmDeltaRoundedText = fmtNum(Math.round(Math.abs(kmDelta)));
    const isLowOriGudang = computedMini.oriNow > 0
      && computedMini.gOri < (computedMini.oriNow * 0.02);
    const isLowRetreadGudang = computedMini.retreadNow > 0
      && computedMini.gRetread < (computedMini.retreadNow * 0.04);
    return [
      {
        title: 'MT Aktif',
        value: fmtNum(computedMini.mtNow),
        delta: `${mtDirection.toUpperCase()} ${signedDeltaText(mtDelta)} (${fmtPct(Math.abs(mtPct))}) DARI POSISI AKHIR ${miniContext?.last_year} (${fmtNum(computedMini.mtLast)} MT).`,
        subtitleGridCols: 1,
        subtitleItems: [
          `${mtDirection.toUpperCase()} ${signedDeltaText(mtDelta)} (${fmtPct(Math.abs(mtPct))})`,
          `DARI POSISI AKHIR ${miniContext?.last_year} (${fmtNum(computedMini.mtLast)} MT).`,
        ],
        subtitleRowGap: 1,
        subtitleLineHeight: 0.9,
        subtitlePaddingTop: 6,
        tone: mtDelta >= 0 ? 'good' : 'risk',
      },
      {
        title: 'Ban On The Fleet',
        value: fmtNum(computedMini.banOtfNow),
        delta: `ORI : ${fmtNum(computedMini.oriNow)} RETREAD : ${fmtNum(computedMini.retreadNow)} OTHER : ${fmtNum(computedMini.otherNow)}`,
        subtitleGridCols: 3,
        subtitleItems: [
          `ORI ${fmtNum(computedMini.oriNow)}`,
          `RETREAD ${fmtNum(computedMini.retreadNow)}`,
          `OTHER ${fmtNum(computedMini.otherNow)}`,
        ],
        tone: 'good',
      },
      {
        title: 'KM Tempuh (YTD)',
        value: fmtNum(computedMini.kmtNow),
        delta: `${kmDirection.toUpperCase()} ${kmDeltaRoundedText} (${fmtPct(Math.abs(kmPct))}) DARI POSISI AKHIR TAHUN ${miniContext?.last_year} (${fmtNum(computedMini.kmtLast)} KM).`,
        subtitleGridCols: 1,
        subtitleItems: [
          `${kmDirection.toUpperCase()} ${kmDeltaRoundedText} (${fmtPct(Math.abs(kmPct))}) PROPORSIONAL`,
          `DARI POSISI AKHIR TAHUN ${miniContext?.last_year} (${fmtNum(computedMini.kmtLast)} KM).`,
        ],
        subtitleRowGap: 1,
        subtitleLineHeight: 0.9,
        subtitlePaddingTop: 6,
        tone: 'warning',
      },
      {
        title: 'Stock Gudang',
        value: fmtNum(computedMini.inventoryNow),
        delta: `ORI:${fmtNum(computedMini.gOri)} RETREAD:${fmtNum(computedMini.gRetread)} CASING:${fmtNum(computedMini.gCasing)} PABRIK:${fmtNum(computedMini.pCasing)} AFKIR:${fmtNum(computedMini.gAfkir)}`,
        subtitleGridCols: 3,
        subtitleItems: [
          { label: `ORI ${fmtNum(computedMini.gOri)}`, onClick: () => handleOpenStokGudangDetail('ori'), disabled: isStokGudangDetailLoading, color: isLowOriGudang ? palette.red : 'inherit' },
          { label: `RETREAD ${fmtNum(computedMini.gRetread)}`, onClick: () => handleOpenStokGudangDetail('retread'), disabled: isStokGudangDetailLoading, color: isLowRetreadGudang ? palette.red : 'inherit' },
          { label: `CASING ${fmtNum(computedMini.gCasing)}`, onClick: () => handleOpenStokGudangDetail('casing'), disabled: isStokGudangDetailLoading },
          { label: `AFKIR ${fmtNum(computedMini.gAfkir)}`, onClick: () => handleOpenStokGudangDetail('afkir'), disabled: isStokGudangDetailLoading },
        ],
        subtitleRowGap: 1,
        subtitleLineHeight: 0.9,
        subtitlePaddingTop: 6,
        tone: 'neutral',
      }
    ];
  }, [computedMini, miniContext, handleOpenStokGudangDetail, isStokGudangDetailLoading]);

  return (
    <div style={{ background: palette.bg, minHeight: '100vh', padding: 16, fontFamily: 'Manrope, Segoe UI, sans-serif' }}>
      <style>{`
        .db-top-hero {
          background: radial-gradient(circle at 10% 10%, rgba(14,165,164,0.20), transparent 55%),
                      radial-gradient(circle at 90% 10%, rgba(37,99,235,0.18), transparent 45%),
                      linear-gradient(135deg, #ffffff 0%, #eef4fb 100%);
          border: 1px solid #d9e2ec;
          border-radius: 22px;
          padding: 18px;
          box-shadow: 0 16px 42px rgba(11,60,93,0.08);
          animation: hero-in 380ms ease-out;
        }
        .db-hero-brand {
          min-width: 0;
        }
        .db-hero-logo {
          display: block;
          width: auto;
          height: clamp(108px, 11vw, 156px);
          max-width: 100%;
          object-fit: contain;
          object-position: left center;
        }
        .db-welcome {
          margin: -2px 2px 14px;
          padding: 10px 14px;
          border-left: 4px solid #0ea5a4;
          border-radius: 0 10px 10px 0;
          background: rgba(255,255,255,.72);
          color: #526273;
          font-size: clamp(14px, 2.2vw, 18px);
          line-height: 1.35;
        }
        .db-welcome strong { color: #0f172a; font-weight: 800; }
        .db-grid-2 {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }
        .db-analytics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 14px;
        }
        .db-top-analytics {
          display: grid;
          grid-template-columns: minmax(0, 6fr) minmax(0, 2fr) minmax(0, 4fr);
          gap: 14px;
        }
        .db-panel {
          min-width: 0;
          overflow: hidden;
        }
        .db-chart-wrap {
          width: 100%;
          position: relative;
        }
        .db-chart-wrap canvas {
          max-width: 100% !important;
        }
        .db-wo-chart { height: 280px; }
        .db-donut-chart { height: 280px; }
        .db-bar-chart { height: 250px; }
        .db-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        .db-table th, .db-table td {
          border-bottom: 1px solid #e2e8f0;
          padding: 8px 6px;
          text-align: left;
        }
        .db-scroll-table-wrap {
          flex: 1;
          min-height: 0;
          overflow: auto;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #fff;
        }
        .db-scroll-table thead th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #0f2d5c;
          color: #fff;
          text-transform: uppercase;
          letter-spacing: .02em;
          font-size: 11px;
        }
        .db-scroll-table tbody tr:nth-child(even) {
          background: #fcfdff;
        }
        .db-lasttrx-modal .modal-content {
          border: 0;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 24px 55px rgba(15, 23, 42, 0.2);
        }
        .db-lasttrx-modal .modal-header {
          border-bottom: 1px solid #e2e8f0;
          background: linear-gradient(180deg, #f8fbff 0%, #f3f7fd 100%);
          padding: 12px 16px;
        }
        .db-lasttrx-meta {
          font-size: 12px;
          color: #64748b;
          margin-top: 2px;
        }
        .db-lasttrx-modal .modal-body {
          padding: 12px 16px 14px;
        }
        .db-lasttrx-table-wrap {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          background: #fff;
        }
        .db-lasttrx-table {
          font-size: 12px;
        }
        .db-lasttrx-table thead th {
          position: sticky;
          top: 0;
          z-index: 1;
          background: #f8fafc;
          text-transform: uppercase;
          letter-spacing: .02em;
          font-size: 11px;
        }
        .db-lasttrx-table tbody tr:nth-child(even) {
          background: #fcfdff;
        }
        .db-chip {
          display: inline-block;
          border-radius: 999px;
          padding: 2px 10px;
          font-size: 11px;
          font-weight: 700;
          background: #fff7ed;
          color: #b45309;
        }
        .db-chip-danger {
          background: #fee2e2;
          color: #b91c1c;
        }
        .db-qa button {
          border: 1px solid #cdd9e5;
          background: #fff;
          border-radius: 12px;
          padding: 10px 12px;
          font-weight: 700;
          color: #0f172a;
          transition: all .22s ease;
          width: 100%;
        }
        .db-qa button:hover {
          transform: translateY(-1px);
          border-color: #0ea5a4;
          box-shadow: 0 8px 18px rgba(14,165,164,0.12);
        }
        .db-refresh-fab {
          position: fixed;
          top: 14px;
          right: 19px;
          width: 46px;
          height: 46px;
          border: 1px solid #111111;
          border-radius: 999px;
          background: #000000;
          color: #ffffff;
          opacity: 0.3;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 1040;
          transition: opacity 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.14);
        }
        .db-refresh-fab:hover {
          opacity: 1;
          transform: translateY(-1px);
          box-shadow: 0 12px 26px rgba(15, 23, 42, 0.2);
        }
        .db-refresh-fab:disabled {
          cursor: wait;
          opacity: 0.55;
        }
        .db-refresh-icon-spin {
          animation: db-refresh-spin 0.85s linear infinite;
        }
        @media (max-width: 991px) {
          .db-grid-2 { grid-template-columns: 1fr; }
          .db-top-analytics { grid-template-columns: 1fr; }
          .db-wo-chart { height: 250px; }
          .db-donut-chart { height: 250px; }
          .db-bar-chart { height: 230px; }
        }
        @media (max-width: 576px) {
          .db-top-hero { padding: 14px; }
          .db-hero-logo { height: clamp(86px, 25vw, 122px); }
          .db-welcome { margin: -4px 0 12px; padding: 9px 12px; }
          .db-wo-chart { height: 220px; }
          .db-donut-chart { height: 220px; }
          .db-bar-chart { height: 210px; }
        }
        @keyframes hero-in {
          from { opacity: .0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes db-refresh-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div
        className="d-flex gap-2"
        style={{
          position: 'fixed',
          top: 10,
          right: 16,
          zIndex: 12000,
        }}
      >
        {pendingRequestCount > 0 && (
          <button
            type="button"
            onClick={handleStopPendingProcess}
            style={topFabStyle}
            aria-label="STOP"
            title={`STOP (${pendingRequestCount})`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="6" width="12" height="12" rx="2" ry="2" />
            </svg>
          </button>
        )}
        <button
          type="button"
          onClick={handleRefreshDashboard}
          title="Refresh"
          aria-label="Refresh dashboard"
          disabled={isRefreshing}
          style={topFabStyle}
        >
          <svg
            className={isRefreshing ? 'db-refresh-icon-spin' : ''}
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 2v6h-6" />
            <path d="M3 12a9 9 0 0 1 15.2-6.36L21 8" />
            <path d="M3 22v-6h6" />
            <path d="M21 12a9 9 0 0 1-15.2 6.36L3 16" />
          </svg>
        </button>
      </div>

      <div className="db-top-hero mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div className="db-hero-brand">
            <img className="db-hero-logo" src="/LogoPUA.png" alt="Putra Utama Anda" />
          </div>
          <div className="db-qa" style={{ minWidth: 260, display: 'grid', gap: 8 }}>
            <button type="button" onClick={() => setPage?.('Page15')}>OTF Event</button>
            <button type="button" onClick={() => setPage?.('Page23')}>Workshop Event</button>
            <div ref={notifDropdownRef} style={{ position: 'relative' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', alignItems: 'stretch', borderRadius: 12, overflow: 'hidden' }}>
                <button type="button" onClick={() => setPage?.('Page33')}>Laporan</button>
                <button
                  type="button"
                  aria-label="Notifikasi Pending"
                  title={`Pending ${pendingAlertsTotal}`}
                  onClick={() => setIsNotifOpen((prev) => !prev)}
                  style={{
                    borderLeft: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 22 22" fill={notifBellColor} aria-hidden="true">
                    <path d="M12 2a6 6 0 0 0-6 6v3.6c0 .8-.3 1.5-.9 2.1L3.2 15.6A1 1 0 0 0 4 17h16a1 1 0 0 0 .8-1.6l-1.9-1.9a3 3 0 0 1-.9-2.1V8a6 6 0 0 0-6-6Zm0 20a3 3 0 0 0 2.8-2H9.2A3 3 0 0 0 12 22Z" />
                  </svg>
                </button>
              </div>
              {isNotifOpen && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 320, background: '#fff', border: `1px solid ${palette.border}`, borderRadius: 10, boxShadow: '0 10px 26px rgba(15, 23, 42, 0.12)', padding: 8, zIndex: 20 }}>
                  {pendingAlertRows.length > 0 ? (
                    <div style={{ display: 'grid', gap: 4 }}>
                      {pendingAlertRows.map((row, idx) => {
                        const total = Number(row?.total || 0);
                        const isDisabled = total <= 0;
                        const textColor = isDisabled ? '#9CA3AF' : palette.ink;
                        return (
                          <button
                            key={`pending-notif-${row?.key || idx}`}
                            type="button"
                            disabled={isDisabled}
                            onClick={() => goToPendingTarget(row)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: 8, border: '1px solid transparent', background: '#fff', textAlign: 'left', width: '100%', color: textColor, fontSize: 12, cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                          >
                            <span style={{ color: textColor }}>{String(row?.label || '-')}</span>
                            <span style={{ fontWeight: 700, color: textColor }}>{total}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ color: '#9CA3AF', fontSize: 12, padding: '6px 8px' }}>
                      Tidak ada data pending.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="db-welcome">
        Selamat datang, <strong>{String(actorNama || '-')}</strong>.
      </div>

      <div style={compactGrid} className="mb-3">
        {summaryCards.map((item) => (
          <MiniCard key={item.title} {...item} />
        ))}
      </div>

      <div className="db-top-analytics mb-3">
        <div className="db-panel" style={{ ...chartCardBase, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <SectionTitle title="Tren KM Tempuh dan AUS Ban" subtitle="Representasi Pendapatan dan Beban Acrual." />
              {isPusatUser && (
                <select
                  value={selectedTrendCabang}
                  onChange={(e) => handleSyncedCabangChange(e.target.value || 'ALL')}
                  style={{
                    width: 170,
                    height: 24,
                    border: `1px solid ${palette.border}`,
                    borderRadius: 0,
                    background: '#fff',
                    color: '#334155',
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '0 6px',
                    marginTop: 1,
                  }}
                  aria-label="Pilih cabang grafik tren"
                >
                  {trendCabangOptions.map((opt) => (
                    <option key={`trend-cabang-${opt.value}`} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', gap: 4, marginTop: 2 }}>
              <div style={{ width: 170, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                <div style={{ color: palette.muted, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', marginBottom: 4 }}>Window data: {trendWindowDays} hari terakhir</div>
                <input
                  type="range"
                  min={10}
                  max={100}
                  step={1}
                  value={trendWindowDays}
                  onChange={(e) => setTrendWindowDays(Math.max(10, Math.min(100, Number(e.target.value || 30))))}
                  style={{ width: '100%', height: 3, accentColor: palette.brand, cursor: 'pointer' }}
                  aria-label="Pilih jumlah hari grafik tren"
                />
                <div style={{ color: palette.muted, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', marginTop: 8, marginBottom: 4 }}>
                  View dari H-{trendWindowOffset+1}
                </div>
                <input
                  type="range"
                  min={0}
                  max={trendMaxOffset}
                  step={1}
                  value={Math.max(0, Math.min(trendMaxOffset, Number(trendWindowOffset) || 0))}
                  onChange={(e) => setTrendWindowOffset(Math.max(0, Math.min(trendMaxOffset, Number(e.target.value || 0))))}
                  style={{ width: '100%', height: 3, accentColor: palette.cyan, cursor: trendMaxOffset > 0 ? 'pointer' : 'not-allowed', opacity: trendMaxOffset > 0 ? 1 : 0.5 }}
                  aria-label="Geser jendela data grafik tren"
                  disabled={trendMaxOffset <= 0}
                />
              </div>
            </div>
          </div>
          <div className="db-chart-wrap db-wo-chart">
            <Line data={trendChartData} options={trendChartOptions} />
          </div>
        </div>

        <div className="db-panel" style={{ ...chartCardBase, padding: 14, display: 'flex', flexDirection: 'column' }}>
          <SectionTitle title="Belanja" subtitle="Year to Date" />
          <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
            {belanjaYtd.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => handleOpenBelanjaDetail(item)}
                disabled={isBelanjaRefreshing || isBelanjaDetailLoading}
                style={{ border: `1px solid ${palette.border}`, borderRadius: 12, padding: 10, background: '#fff', width: '100%', textAlign: 'left', opacity: (isBelanjaRefreshing || isBelanjaDetailLoading) ? 0.7 : 1 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                  <span style={{ color: palette.muted, fontSize: 12, fontWeight: 700 }}>{item.key}</span>
                  <span style={{ color: palette.ink, fontSize: 18, fontWeight: 800 }}>{Number(item.value || 0).toLocaleString('id-ID')}</span>
                </div>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ height: 6, background: '#e8eef6', borderRadius: 999, flex: 1 }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${getBelanjaProgressWidth(item)}%`,
                        borderRadius: 999,
                        background: item.color,
                      }}
                    />
                  </div>
                  <span style={{ color: palette.muted, fontSize: 12, fontWeight: 300, minWidth: 34, textAlign: 'right' }}>
                    {getBelanjaProgressPercentText(item)}
                  </span>
                </div>
              </button>
            ))}
          </div>
          <div className="d-flex flex-column mt-auto pt-3" style={{ gap: 8 }}>
            <button type="button" className="btn btn-outline-primary btn-sm w-100" onClick={() => setShowMtPicker(true)}>Lihat MT</button>
            <button type="button" className="btn btn-outline-success btn-sm w-100" onClick={() => setShowBanPicker(true)}>Lihat Ban</button>
            <button type="button" className="btn btn-outline-dark btn-sm w-100" onClick={openLastBanTrxModal}>30 Last Trx</button>
          </div>
        </div>

        <div className="db-panel" style={{ ...chartCardBase, padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <SectionTitle title="Komposisi Status Ban" subtitle={`Total Ban : ${fmtNum(computedKomposisi.tireUnderMgmt)}`} />
            {isPusatUser && (
              <select
                value={String(selectedKomposisiCabang || 'C-1')}
                onChange={(e) => handleSyncedCabangChange(e.target.value || 'C-1')}
                style={{
                  width: 180,
                  height: 24,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 0,
                  background: '#fff',
                  color: '#334155',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '0 6px',
                  marginTop: 1,
                }}
                aria-label="Pilih cabang komposisi status ban"
              >
                {monitoringCabangOptions.map((opt) => (
                  <option key={`komposisi-cabang-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="db-chart-wrap db-donut-chart">
            <Doughnut
              data={komposisiBanData}
              options={{
                ...baseOptions,
                scales: undefined,
                plugins: {
                  ...baseOptions.plugins,
                  legend: { display: false },
                },
                cutout: '62%',
              }}
              aria-label="Komposisi status ban"
              role="img"
            />
          </div>
          <div className="mt-2" style={{ color: palette.muted, fontSize: 12, fontWeight: 600, textAlign: 'center' }}>
            {`OTF ${fmtNum(computedKomposisi.banOtfNow)} | GUDANG ${fmtNum(computedKomposisi.gudangTotal)} ${computedKomposisi.includePabrik ? `| PABRIK` : ''} ${computedKomposisi.includePabrik ? fmtNum(computedKomposisi.pCasing) : ''}`}
          </div>
        </div>
      </div>

      {false && (
        <div className="db-analytics-grid mb-3">
          <div className="db-panel" style={{ ...chartCardBase, padding: 14 }}>
            <SectionTitle title="Produktivitas Cabang" subtitle="Realisasi WO mingguan" />
            <div className="db-chart-wrap db-bar-chart">
              <Bar
                data={cabangProductivity}
                options={{
                  ...baseOptions,
                  plugins: { ...baseOptions.plugins, legend: { display: false } },
                }}
              />
            </div>
          </div>

          <div className="db-panel" style={{ ...chartCardBase, padding: 14 }}>
            <SectionTitle title="Unit Risiko Tertinggi" subtitle="Keterlambatan last check ODO/TD" />
            <div className="db-chart-wrap db-bar-chart">
              <Bar
                data={riskUnits}
                options={{
                  ...baseOptions,
                  indexAxis: 'y',
                  plugins: { ...baseOptions.plugins, legend: { display: false } },
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="db-grid-2">
        <div style={{ ...chartCardBase, padding: 14, width: '100%', height: 450, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
            <SectionTitle title="Monitoring Ban" subtitle="Kondisi ban yang perlu perhatian." />
            {isPusatUser && (
              <select
                value={String(selectedMonitoringCabang || 'C-1')}
                onChange={(e) => handleSyncedCabangChange(e.target.value || 'C-1')}
                style={{
                  width: 180,
                  height: 24,
                  border: `1px solid ${palette.border}`,
                  borderRadius: 0,
                  background: '#fff',
                  color: '#334155',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '0 6px',
                  marginTop: 1,
                }}
                aria-label="Pilih cabang monitoring ban"
              >
                {monitoringCabangOptions.map((opt) => (
                  <option key={`monitoring-cabang-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="table-responsive db-scroll-table-wrap">
            <table className="db-table db-scroll-table">
              <thead>
                <tr>
                  <th>Kondisi</th>
                  <th>Cabang</th>
                  <th>Jumlah</th>
                  <th>Refresh</th>
                </tr>
              </thead>
              <tbody>
                {monitoringBanRows.map((row) => (
                  <tr key={row.kondisi}>
                    <td>{row.kondisi}</td>
                    <td>{row.cabang}</td>
                    <td>
                      {(() => {
                        const jumlahRaw = row.jumlah;
                        const jumlahNum = Number(String(jumlahRaw).replace(/\./g, ''));
                        const isNoData = String(jumlahRaw || '').trim().toLowerCase() === 'nodata';
                        if (isNoData || !Number.isFinite(jumlahNum)) return String(jumlahRaw || 'nodata');
                        const isZero = jumlahNum === 0;
                        const isMtNonAktifRow = row.kondisi === MONITORING_MT_NONAKTIF_CONDITION;
                        return (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={() => openMonitoringDetailModal(row.kondisi)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openMonitoringDetailModal(row.kondisi);
                              }
                            }}
                            style={{
                              display: 'inline-block',
                              minWidth: 36,
                              textAlign: 'center',
                              borderRadius: 999,
                              padding: '2px 10px',
                              fontWeight: 700,
                              background: isMtNonAktifRow ? '#E5E7EB' : (isZero ? '#DCFCE7' : '#FEE2E2'),
                              color: isMtNonAktifRow ? '#374151' : (isZero ? '#166534' : '#991B1B'),
                              cursor: 'pointer',
                            }}
                          >
                            {String(jumlahRaw)}
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        aria-label="Refresh monitoring ban"
                        title="Refresh monitoring ban"
                        onClick={() => handleRefreshMonitoringRow(row.kondisi)}
                        disabled={refreshingMonitoringKey === row.kondisi}
                        style={{
                          width: 30,
                          height: 25,
                          borderRadius: '999px',
                          background: '#DBEAFE',
                          border: 'none',
                          padding: 0,
                          lineHeight: 1,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: refreshingMonitoringKey === row.kondisi ? 0.6 : 1,
                        }}
                      >
                        <svg className={refreshingMonitoringKey === row.kondisi ? 'db-refresh-icon-spin' : ''} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={palette.blue} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M21 2v6h-6" />
                          <path d="M3 22v-6h6" />
                          <path d="M21 8a9 9 0 0 0-15-3.36L3 8" />
                          <path d="M3 16a9 9 0 0 0 15 3.36L21 16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ ...chartCardBase, padding: 14, width: '100%', height: 450, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'space-between', gap: 10 }}>
            <SectionTitle title="Daftar Hadir Petugas" subtitle={`Tanggal ${daftarHadirSubtitle}`} />
            
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', gap: 6, minHeight: '100%' }}>
            {isPusatUser && daftarHadirCabangOptions.length > 0 && (
                <select
                  value={String(selectedDaftarHadirCabang || '')}
                  onChange={(e) => setSelectedDaftarHadirCabang(String(e.target.value || ''))}
                  style={{
                    width: 180,
                    height: 24,
                    border: `1px solid ${palette.border}`,
                    borderRadius: 0,
                    background: '#fff',
                    color: '#334155',
                    fontSize: 11,
                    fontWeight: 600,
                    padding: '0 6px',
                    marginTop: 1,
                  }}
                  aria-label="Pilih cabang daftar hadir"
                >
                  {daftarHadirCabangOptions.map((opt) => (
                    <option key={`daftarhadir-cabang-${opt.value}`} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>)}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={handleOpenAbsensiBulanan}
                    disabled={isAbsensiBulananLoading}
                    style={{
                      display: 'inline-block',
                      textAlign: 'center',
                      borderRadius: 999,
                      padding: '2px 10px',
                      fontWeight: 700,
                      fontSize: 11,
                      lineHeight: 1.2,
                      background: isAbsensiBulananLoading ? '#E2E8F0' : '#DBEAFE',
                      color: isAbsensiBulananLoading ? '#64748B' : '#1D4ED8',
                      cursor: isAbsensiBulananLoading ? 'wait' : 'pointer',
                      border: 'none',
                    }}
                  >
                    {isAbsensiBulananLoading ? 'Memuat...' : 'Absensi Bulanan'}
                  </button>
                  {canOpenSupervisiAbsensi && (
                    <>
                      <button
                        type="button"
                        onClick={handleOpenVerifikasiAbsensi}
                        style={{
                          display: 'inline-block',
                          textAlign: 'center',
                          borderRadius: 999,
                          padding: '2px 10px',
                          fontWeight: 700,
                          fontSize: 11,
                          lineHeight: 1.2,
                          background: hasBelumAbsenRow ? '#FEE2E2' : '#DCFCE7',
                          color: hasBelumAbsenRow ? '#991B1B' : '#166534',
                          cursor: 'pointer',
                          border: 'none',
                        }}
                      >
                        Supervisi
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenAbsenTerjadwal}
                        style={{
                          display: 'inline-block',
                          textAlign: 'center',
                          borderRadius: 999,
                          padding: '2px 10px',
                          fontWeight: 700,
                          fontSize: 11,
                          lineHeight: 1.2,
                          background: '#FEF3C7',
                          color: '#92400E',
                          cursor: 'pointer',
                          border: 'none',
                        }}
                      >
                        Cuti/Libur/DL
                      </button>
                    </>
                  )}
                </div>
              </div>
            
          </div>
          <div className="table-responsive db-scroll-table-wrap">
            <table className="db-table db-scroll-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Cabang</th>
                  <th>Nama</th>
                  <th>Status Absen</th>
                  <th>Jam Absen</th>
                  <th>Lokasi</th>
                  <th>Foto</th>
                </tr>
              </thead>
              <tbody>
                {daftarHadirTodayRows.length > 0 ? daftarHadirTodayRows.map((row, idx) => (
                  <tr key={`${row.nama}-${row.jamAbsen}-${idx}`}>
                    <td>{idx + 1}</td>
                    <td>{row.cabang}</td>
                    <td>{row.nama}</td>
                    <td style={String(row.statusAbsen || '').trim().toUpperCase() === 'BELUM ABSEN' ? { color: palette.red, fontWeight: 700 } : undefined}>
                      {row.statusAbsen}
                    </td>
                    <td>{row.jamAbsen}</td>
                    <td>{row.lokasi}</td>
                    <td style={{ textAlign: 'center' }}>
                      {row.foto && row.foto !== '-' ? (() => {
                        const fotoUrl = buildDaftarHadirFotoUrl(row.foto);
                        const initial = String(row.nama || '?').trim().charAt(0).toUpperCase() || '?';
                        return (
                          <button
                            type="button"
                            onClick={() => openDaftarHadirFotoModal(row)}
                            title={`Lihat foto absensi ${row.nama}`}
                            aria-label={`Lihat foto absensi ${row.nama}`}
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: '999px',
                              overflow: 'hidden',
                              border: `1px solid ${palette.border}`,
                              padding: 0,
                              background: '#E2E8F0',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                          >
                            <img
                              src={fotoUrl}
                              alt={`Foto absensi ${row.nama}`}
                              loading="lazy"
                              decoding="async"
                              referrerPolicy="no-referrer"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                const fallback = e.currentTarget.nextSibling;
                                if (fallback) fallback.style.display = 'flex';
                              }}
                            />
                            <span
                              aria-hidden="true"
                              style={{
                                display: 'none',
                                width: '100%',
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: palette.brand,
                                fontSize: 12,
                                fontWeight: 700,
                              }}
                            >
                              {initial}
                            </span>
                          </button>
                        );
                      })() : '-'}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', color: palette.muted }}>
                      Tidak ada data hadir untuk hari ini.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ModalCariMT
        open={showMtPicker}
        onClose={() => setShowMtPicker(false)}
        showNotif={showNotif}
        globalOpsi={globalOpsi}
        kodeCabang=""
        onSelect={async (mt) => {
          const idMt = String(mt?.id_mt || '').trim();
          if (!idMt) return;
          const kodeCabangMt = String(mt?.kode_cabang || '').trim();
          let mtRowDetail = null;
          try {
            const payload = {
              page: 1,
              pageSize: 20,
              kodeCabang: kodeCabangMt || '',
              searchBy: 'id_mt',
              keyword: idMt,
              sortBy: 'id_mt',
              sortDir: 'asc',
            };
            const { data } = await api.post(`${backendPort}/api/main/load_page9_mt_list`, payload, { timeout: 20000 });
            const rows = Array.isArray(data?.rows) ? data.rows : [];
            mtRowDetail = rows.find((x) => String(x?.id_mt || '').trim() === idMt) || null;
          } catch (_err) {}
          if (typeof window !== 'undefined') {
            window.__dashboard_picker_state = {
              ...(window.__dashboard_picker_state || {}),
              id_mt: idMt,
              mt_row: mtRowDetail,
            };
          }
          setPage?.('Page9_Show');
        }}
      />

      <ModalCariBan
        open={showBanPicker}
        onClose={() => setShowBanPicker(false)}
        showNotif={showNotif}
        globalOpsi={globalOpsi}
        kodeCabang=""
        onSelect={(ban) => {
          const idBan = Number(ban?.id_ban || 0);
          if (!Number.isFinite(idBan) || idBan <= 0) return;
          setSelectedBanId(idBan);
          setShowBanPicker(false);
          setShowBanDetailModal(true);
        }}
      />
      <ModalBanShow
        open={showBanDetailModal}
        onClose={() => setShowBanDetailModal(false)}
        globalOpsi={globalOpsi}
        idBan={selectedBanId}
        selectedPosisi=""
      />
      <ShowPic
        open={showDaftarHadirFotoModal}
        onClose={() => {
          setShowDaftarHadirFotoModal(false);
          setSelectedDaftarHadirFotoFiles([]);
        }}
        row={{}}
        backendPort={backendPort}
        token=""
        readOnly={true}
        customFiles={selectedDaftarHadirFotoFiles}
        customTitle="Foto Absensi"
        zIndex={1085}
        fitImageToFrame={true}
      />
      <ShowPic
        open={showAbsenTerjadwalLampiranModal}
        onClose={() => {
          setShowAbsenTerjadwalLampiranModal(false);
          setSelectedAbsenTerjadwalLampiranFiles([]);
        }}
        row={{ catatan: selectedAbsenTerjadwalLampiranFiles?.[0]?.caption || "" }}
        backendPort={backendPort}
        token=""
        readOnly={true}
        customFiles={selectedAbsenTerjadwalLampiranFiles}
        customTitle="Lampiran Absen Terjadwal"
        customNote={selectedAbsenTerjadwalLampiranFiles?.[0]?.caption || ""}
        customFileUrl={selectedAbsenTerjadwalLampiranFiles?.[0]?.url || null}
        zIndex={1085}
      />

      {showAbsenTerjadwalModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowAbsenTerjadwalModal(false)} />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowAbsenTerjadwalModal(false);
            }}
          >
            <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document" style={{ width: '88vw', maxWidth: '88vw' }}>
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <div>
                    <h5 className="modal-title mb-0">DAFTAR ABSEN TERJADWAL</h5>
                    <div className="small text-muted mt-1">{`Cabang ${supervisiTargetCabangName}`}</div>
                  </div>
                  <button type="button" className="btn-close mt-0" onClick={() => setShowAbsenTerjadwalModal(false)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  {isPusatUser && (
                    <>
                      <div className="row g-2 align-items-end">
                        <div className="col-12 col-lg-3">
                          <label className="form-label mb-1">Nama Pegawai</label>
                          <select
                            className="form-select form-select-sm"
                            value={absenTerjadwalForm.pegawaiKey}
                            onChange={(e) => updateAbsenTerjadwalForm('pegawaiKey', e.target.value)}
                            disabled={absenTerjadwalSubmitting}
                          >
                            <option value="">Pilih Pegawai</option>
                            {absenTerjadwalPegawaiOptions.map((pegawai) => (
                              <option key={pegawai.key} value={pegawai.key}>
                                {`${pegawai.nama} - ${pegawai.cabang}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-md-6 col-lg-2">
                          <label className="form-label mb-1">Kategori</label>
                          <select
                            className="form-select form-select-sm"
                            value={absenTerjadwalForm.kategori}
                            onChange={(e) => updateAbsenTerjadwalForm('kategori', e.target.value)}
                            disabled={absenTerjadwalSubmitting}
                          >
                            {ABSEN_TERJADWAL_STATUS_OPTIONS.map((status) => (
                              <option key={`absen-terjadwal-status-${status}`} value={status}>{status}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-12 col-md-3 col-lg-2">
                          <label className="form-label mb-1">Tanggal Mulai</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={absenTerjadwalForm.tanggalMulai}
                            onChange={(e) => updateAbsenTerjadwalForm('tanggalMulai', e.target.value)}
                            disabled={absenTerjadwalSubmitting}
                          />
                        </div>
                        <div className="col-12 col-md-3 col-lg-2">
                          <label className="form-label mb-1">Tanggal Selesai</label>
                          <input
                            type="date"
                            className="form-control form-control-sm"
                            value={absenTerjadwalForm.tanggalSelesai}
                            onChange={(e) => updateAbsenTerjadwalForm('tanggalSelesai', e.target.value)}
                            disabled={absenTerjadwalSubmitting}
                          />
                        </div>
                        <div className="col-12 col-md-6 col-lg-1">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm w-100"
                            style={{ whiteSpace: 'nowrap' }}
                            onClick={openAbsenTerjadwalKeteranganModal}
                            disabled={absenTerjadwalSubmitting}
                          >
                            {`Keterangan${absenTerjadwalAttachments.length > 0 ? ` +${absenTerjadwalAttachments.length}` : ''}`}
                          </button>
                        </div>
                        <div className="col-12 col-md-6 col-lg-2">
                          <button
                            type="button"
                            className="btn btn-primary btn-sm w-100"
                            onClick={handleSubmitAbsenTerjadwal}
                            disabled={absenTerjadwalSubmitting}
                          >
                            {absenTerjadwalSubmitting ? 'Menyimpan...' : 'Simpan'}
                          </button>
                        </div>
                      </div>

                      <div
                        aria-hidden="true"
                        style={{
                          width: '100%',
                          height: 1,
                          background: '#CBD5E1',
                          margin: '18px 0 14px',
                        }}
                      />
                    </>
                  )}

                  <div className="row g-2 align-items-end">
                    <div className="col-auto">
                      <div className="fw-semibold small">Tampilkan Periode :</div>
                    </div>
                    <div className="col-12 col-md-3 col-lg-2">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        aria-label="Tanggal Awal"
                        value={absenTerjadwalPeriode.tanggalAwal}
                        onChange={(e) => setAbsenTerjadwalPeriode((prev) => ({ ...prev, tanggalAwal: e.target.value }))}
                      />
                    </div>
                    <div className="col-auto pb-1">sd</div>
                    <div className="col-12 col-md-3 col-lg-2">
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        aria-label="Tanggal Akhir"
                        value={absenTerjadwalPeriode.tanggalAkhir}
                        onChange={(e) => setAbsenTerjadwalPeriode((prev) => ({ ...prev, tanggalAkhir: e.target.value }))}
                      />
                    </div>
                    <div className="col-12 col-md-2">
                      <button type="button" className="btn btn-outline-primary btn-sm w-100" onClick={handleTampilkanAbsenTerjadwal}>
                        Tampilkan
                      </button>
                    </div>
                    <div className="col-12 col-md-auto ms-md-auto">
                      <input
                        id="absen-terjadwal-filter"
                        type="search"
                        className="form-control form-control-sm"
                        style={{ minWidth: 220 }}
                        placeholder="Cari..."
                        value={absenTerjadwalFilter}
                        onChange={(e) => setAbsenTerjadwalFilter(e.target.value)}
                        aria-label="Filter daftar absen terjadwal"
                      />
                    </div>
                  </div>

                  <div className="table-responsive mt-3" style={{ maxHeight: '42vh' }}>
                    <table className="table table-sm table-bordered align-middle mb-0">
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#0f2d5c', color: '#ffffff' }}>
                        <tr>
                          <th style={{ width: 54, background: '#0f2d5c', color: '#ffffff' }}>No.</th>
                          <th style={{ background: '#0f2d5c', color: '#ffffff' }}>Nama Pegawai</th>
                          <th style={{ background: '#0f2d5c', color: '#ffffff' }}>Cabang</th>
                          <th style={{ background: '#0f2d5c', color: '#ffffff' }}>Status Absen</th>
                          <th style={{ background: '#0f2d5c', color: '#ffffff' }}>Mulai</th>
                          <th style={{ background: '#0f2d5c', color: '#ffffff' }}>Selesai</th>
                          <th style={{ background: '#0f2d5c', color: '#ffffff' }}>Keterangan</th>
                          <th style={{ width: 110, textAlign: 'center', background: '#0f2d5c', color: '#ffffff' }}>Lampiran</th>
                          <th style={{ width: 90, textAlign: 'center', background: '#0f2d5c', color: '#ffffff' }}>Hapus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {absenTerjadwalLoading ? (
                          <tr>
                            <td colSpan={9} className="text-center text-muted py-3">Memuat data...</td>
                          </tr>
                        ) : paginatedAbsenTerjadwalRows.map((row, idx) => {
                          const lampiranCount = Array.isArray(row?.lampiran) ? row.lampiran.length : 0;
                          const rowNumber = ((absenTerjadwalCurrentPage - 1) * 10) + idx + 1;
                          return (
                            <tr key={row.id}>
                              <td>{rowNumber}</td>
                              <td>{row.nama || '-'}</td>
                              <td>{row.cabang || '-'}</td>
                              <td>{row.statusAbsen || '-'}</td>
                              <td>{row.tanggalMulai || '-'}</td>
                              <td>{row.tanggalSelesai || '-'}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.keterangan || '-'}</td>
                              <td className="text-center">
                                {lampiranCount > 0 ? (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm"
                                    onClick={() => openAbsenTerjadwalLampiran(row)}
                                  >
                                    {`Lihat (${lampiranCount})`}
                                  </button>
                                ) : '-'}
                              </td>
                              <td className="text-center">
                                {isPusatUser ? (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => setAbsenTerjadwalDeleteTarget(row)}
                                    disabled={absenTerjadwalDeleting}
                                  >
                                    Hapus
                                  </button>
                                ) : '-'}
                              </td>
                            </tr>
                          );
                        })}
                        {!absenTerjadwalLoading && filteredAbsenTerjadwalRows.length === 0 && (
                          <tr>
                            <td colSpan={9} className="text-center text-muted py-3">Tidak ada data.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  {!absenTerjadwalLoading && filteredAbsenTerjadwalRows.length > 0 && (
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                      <div className="small text-muted">
                        {`Menampilkan ${((absenTerjadwalCurrentPage - 1) * 10) + 1}-${Math.min(absenTerjadwalCurrentPage * 10, filteredAbsenTerjadwalRows.length)} dari ${filteredAbsenTerjadwalRows.length} baris`}
                      </div>
                      <nav aria-label="Paginasi daftar absen terjadwal">
                        <ul className="pagination pagination-sm mb-0">
                          <li className={`page-item${absenTerjadwalCurrentPage === 1 ? ' disabled' : ''}`}>
                            <button
                              type="button"
                              className="page-link"
                              onClick={() => setAbsenTerjadwalPage((page) => Math.max(1, page - 1))}
                              disabled={absenTerjadwalCurrentPage === 1}
                            >
                              Sebelumnya
                            </button>
                          </li>
                          <li className="page-item active" aria-current="page">
                            <span className="page-link">{`${absenTerjadwalCurrentPage} / ${absenTerjadwalTotalPages}`}</span>
                          </li>
                          <li className={`page-item${absenTerjadwalCurrentPage === absenTerjadwalTotalPages ? ' disabled' : ''}`}>
                            <button
                              type="button"
                              className="page-link"
                              onClick={() => setAbsenTerjadwalPage((page) => Math.min(absenTerjadwalTotalPages, page + 1))}
                              disabled={absenTerjadwalCurrentPage === absenTerjadwalTotalPages}
                            >
                              Berikutnya
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowAbsenTerjadwalModal(false)}
                  >
                    Tutup
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {absenTerjadwalDeleteTarget && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1065 }}
            onClick={() => setAbsenTerjadwalDeleteTarget(null)}
          />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1070 }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Konfirmasi Hapus</h5>
                  <button type="button" className="btn-close" onClick={() => setAbsenTerjadwalDeleteTarget(null)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <p className="mb-2">Anda Akan menghapus List Absen Terjadwal. Penghapusan ini berlaku mulai tanggal penghapusan</p>
                  <div className="small text-muted">
                    {`${absenTerjadwalDeleteTarget?.nama || '-'} | ${absenTerjadwalDeleteTarget?.statusAbsen || '-'} | ${absenTerjadwalDeleteTarget?.tanggalMulai || '-'} sd ${absenTerjadwalDeleteTarget?.tanggalSelesai || '-'}`}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setAbsenTerjadwalDeleteTarget(null)} disabled={absenTerjadwalDeleting}>
                    Batal
                  </button>
                  <button type="button" className="btn btn-danger btn-sm" onClick={handleConfirmDeleteAbsenTerjadwal} disabled={absenTerjadwalDeleting}>
                    {absenTerjadwalDeleting ? 'Menghapus...' : 'Hapus'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showAbsenTerjadwalKeteranganModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1065 }}
            onClick={closeAbsenTerjadwalKeteranganModal}
          />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1070 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) closeAbsenTerjadwalKeteranganModal();
            }}
          >
            <div className="modal-dialog modal-dialog-scrollable" role="document" style={{ width: '62vw', maxWidth: '62vw' }}>
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <div>
                    <h5 className="modal-title mb-0">KETERANGAN ABSEN TERJADWAL</h5>
                    <div className="small text-muted mt-1">
                      {selectedAbsenTerjadwalPegawai
                        ? `${selectedAbsenTerjadwalPegawai.nama} - ${selectedAbsenTerjadwalPegawai.cabang}`
                        : 'Pilih pegawai'}
                    </div>
                  </div>
                  <button type="button" className="btn-close mt-0" onClick={closeAbsenTerjadwalKeteranganModal} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label mb-1">Keterangan</label>
                    <textarea
                      rows={2}
                      className="form-control form-control-sm"
                      style={{ backgroundColor: '#E0F2FE', borderColor: '#7DD3FC' }}
                      value={absenTerjadwalKeteranganDraft}
                      onChange={(e) => setAbsenTerjadwalKeteranganDraft(e.target.value)}
                    />
                  </div>
                  <div className="px-3 py-2 rounded border bg-white">
                    <div className="small fw-semibold text-secondary mb-2">Upload Dokumen</div>
                    {absenTerjadwalInvalidCaptionIndexes.length > 0 ? (
                      <div className="alert alert-warning py-2 mb-2">
                        Caption dokumen wajib diisi untuk semua lampiran.
                      </div>
                    ) : null}
                    <LampiranUpload
                      attachments={absenTerjadwalLampiranDraft}
                      setAttachments={setAbsenTerjadwalLampiranDraft}
                      bisaInput={true}
                      uploadInputClassName="dashboard-supervisi-upload-input"
                      invalidCaptionIndexes={absenTerjadwalInvalidCaptionIndexes}
                      onCaptionChange={() => {
                        if (absenTerjadwalInvalidCaptionIndexes.length > 0) {
                          setAbsenTerjadwalInvalidCaptionIndexes([]);
                        }
                      }}
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={closeAbsenTerjadwalKeteranganModal}>
                      Batal
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={saveAbsenTerjadwalKeteranganDraft}>
                      Lanjutkan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showLastBanTrxModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowLastBanTrxModal(false)} />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowLastBanTrxModal(false);
            }}
          >
            <div className="modal-dialog modal-xl modal-dialog-scrollable db-lasttrx-modal" role="document">
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <div>
                    <h5 className="modal-title mb-0">30 Transaksi Terakhir</h5>
                    <div className="db-lasttrx-meta">Riwayat event ban terbaru.</div>
                  </div>
                  <button type="button" className="btn-close mt-0" onClick={() => setShowLastBanTrxModal(false)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  {lastBanTrxError ? (
                    <div className="alert alert-danger py-2 mb-2">{lastBanTrxError}</div>
                  ) : null}
                  <div className="db-lasttrx-table-wrap">
                    <div className="table-responsive">
                      <table className="db-table db-lasttrx-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                      <colgroup>
                        <col style={{ width: '4%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '8%' }} />
                        <col style={{ width: '4%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '10%' }} />
                        <col style={{ width: '14%' }} />
                        <col style={{ width: '28%' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>NO</th>
                          <th>Cabang</th>
                          <th>Tanggal</th>
                          <th>Jam</th>
                          <th>User</th>
                          <th>No. Seri Ban</th>
                          <th>Event ban</th>
                          <th>Keterangan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lastBanTrxLoading ? (
                          <tr>
                            <td colSpan={8} className="text-center py-3">Loading...</td>
                          </tr>
                        ) : lastBanTrxRows.length > 0 ? (
                          lastBanTrxRows.map((row, idx) => (
                            <tr key={`last-ban-trx-${row?.id_event || idx}`}>
                              <td style={{ whiteSpace: 'nowrap' }}>{idx + 1}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {mapNamaCabang.get(String(row?.cabang || '').trim()) || String(row?.cabang || '-')}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(row?.update_at)}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(row?.update_at)}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{String(row?.user || '-')}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{String(row?.seri_ban || '-')}</td>
                              <td style={{ whiteSpace: 'nowrap' }}>{String(row?.event || '-')}</td>
                              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{normalizeLastTrxInfo(row?.keterangan)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={8} className="text-center py-3">Tidak ada data.</td>
                          </tr>
                        )}
                      </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showStokGudangDetailModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowStokGudangDetailModal(false)} />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowStokGudangDetailModal(false);
            }}
          >
            <div className="modal-dialog modal-dialog-scrollable" role="document" style={{ width: '90vw', maxWidth: '90vw' }}>
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <div>
                    <h5 className="modal-title mb-1">{`Stok Gudang - ${stokGudangDetailMeta.category}`}</h5>
                    <div className="text-muted small">
                      {`${stokGudangDetailMeta.namaCabang} | Minicard ${fmtNum(stokGudangDetailMeta.miniCount)} | Data realtime ${fmtNum(stokGudangDetailMeta.realtimeCount)}`}
                    </div>
                  </div>
                  <button type="button" className="btn-close mt-0" onClick={() => setShowStokGudangDetailModal(false)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <PageKonten34_ShowDataLaporan
                    opsiCabang={Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : []}
                    opsiBan={Array.isArray(globalOpsi?.opsiBan) ? globalOpsi.opsiBan : []}
                    globalOpsi={globalOpsi}
                    setPage={(targetPage) => {
                      if (targetPage === 'Page33') {
                        setShowStokGudangDetailModal(false);
                        return;
                      }
                      setPage?.(targetPage);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showBelanjaDetailModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowBelanjaDetailModal(false)} />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowBelanjaDetailModal(false);
            }}
          >
            <div className="modal-dialog modal-dialog-scrollable" role="document" style={{ width: '90vw', maxWidth: '90vw' }}>
              <div className="modal-content">
                <div className="modal-body position-relative">
                  <button
                    type="button"
                    className="btn-close position-absolute"
                    style={{ top: 14, right: 14, zIndex: 2 }}
                    onClick={() => setShowBelanjaDetailModal(false)}
                    aria-label="Close"
                  />
                  <PageKonten34_ShowDataLaporan
                    opsiCabang={Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : []}
                    opsiBan={Array.isArray(globalOpsi?.opsiBan) ? globalOpsi.opsiBan : []}
                    globalOpsi={globalOpsi}
                    setPage={(targetPage) => {
                      if (targetPage === 'Page33') {
                        setShowBelanjaDetailModal(false);
                        return;
                      }
                      setPage?.(targetPage);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showMonitoringDetailModal && (
        <>
          <div className="modal-backdrop fade show" onClick={closeMonitoringDetailToDashboard} />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeMonitoringDetailToDashboard();
            }}
          >
            <div className="modal-dialog modal-dialog-scrollable" role="document" style={{ width: '90vw', maxWidth: '90vw' }}>
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <h5 className="modal-title mb-0">Detail Monitoring Ban</h5>
                  <button type="button" className="btn-close mt-0" onClick={closeMonitoringDetailToDashboard} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <PageKonten34_ShowDataLaporan
                    opsiCabang={Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : []}
                    opsiBan={Array.isArray(globalOpsi?.opsiBan) ? globalOpsi.opsiBan : []}
                    globalOpsi={globalOpsi}
                    setPage={(targetPage) => {
                      if (targetPage === 'Page33') {
                        closeMonitoringDetailToDashboard();
                        return;
                      }
                      setPage?.(targetPage);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showDaftarHadirBulananModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowDaftarHadirBulananModal(false)} />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowDaftarHadirBulananModal(false);
            }}
          >
            <div className="modal-dialog modal-dialog-scrollable" role="document" style={{ width: '90vw', maxWidth: '90vw' }}>
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <h5 className="modal-title mb-0">Absensi Bulanan</h5>
                  <button type="button" className="btn-close mt-0" onClick={() => setShowDaftarHadirBulananModal(false)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <PageKonten34_ShowDataLaporan
                    opsiCabang={Array.isArray(globalOpsi?.opsiCabang) ? globalOpsi.opsiCabang : []}
                    opsiBan={Array.isArray(globalOpsi?.opsiBan) ? globalOpsi.opsiBan : []}
                    globalOpsi={globalOpsi}
                    setPage={(targetPage) => {
                      if (targetPage === 'Page33') {
                        setShowDaftarHadirBulananModal(false);
                        return;
                      }
                      setPage?.(targetPage);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showSupervisiAbsensiModal && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowSupervisiAbsensiModal(false)} />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowSupervisiAbsensiModal(false);
            }}
          >
            <div className="modal-dialog modal-dialog-scrollable" role="document" style={{ width: '70vw', maxWidth: '70vw' }}>
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <div>
                    <h5 className="modal-title mb-0">{`SUPERVISI ABSENSI CABANG ${supervisiTargetCabangName}`}</h5>
                    <div className="small text-muted mt-1">{`Tanggal ${formatTanggalPanjang(supervisiTanggal)}`}</div>
                  </div>
                  <button type="button" className="btn-close mt-0" onClick={() => setShowSupervisiAbsensiModal(false)} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <div className="row g-2 align-items-end mb-3">
                    <div className="col-md-3">
                      <label className="form-label mb-1">Tanggal</label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={supervisiTanggal}
                        onChange={(e) => setSupervisiTanggal(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm w-100"
                        onClick={() => loadSupervisiAbsensi(supervisiTanggal)}
                        disabled={supervisiLoading || supervisiSubmitting}
                      >
                        {supervisiLoading ? 'Memuat...' : 'Muat Data'}
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive" style={{ maxHeight: '60vh' }}>
                    <table className="table table-sm table-bordered align-middle mb-0">
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2, background: '#0f2d5c', color: '#ffffff' }}>
                        <tr>
                          <th style={{ whiteSpace: 'nowrap', background: '#0f2d5c', color: '#ffffff' }}>NAMA PEGAWAI</th>
                          <th style={{ whiteSpace: 'nowrap', background: '#0f2d5c', color: '#ffffff' }}>JAM ABSEN</th>
                          <th style={{ whiteSpace: 'nowrap', background: '#0f2d5c', color: '#ffffff' }}>LOKASI</th>
                          <th style={{ whiteSpace: 'nowrap', background: '#0f2d5c', color: '#ffffff' }}>STATUS ABSEN</th>
                          <th style={{ whiteSpace: 'nowrap', background: '#0f2d5c', color: '#ffffff' }}>KETERANGAN</th>
                          <th style={{ width: 72, whiteSpace: 'nowrap', textAlign: 'center', background: '#0f2d5c', color: '#ffffff' }}>FOTO</th>
                          <th style={{ width: 80, whiteSpace: 'nowrap', textAlign: 'center', background: '#0f2d5c', color: '#ffffff' }}>
                            <input
                              type="checkbox"
                              checked={supervisiAllChecked}
                              disabled={supervisiSelectableKeys.length <= 0}
                              onChange={(e) => handleToggleAllSupervisi(e.target.checked)}
                            />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {supervisiRows.map((row) => {
                          const selectedStatus = String(supervisiStatusMap?.[row.key] || '').trim().toUpperCase();
                          const canCheckRow = !!selectedStatus;
                          const canOpenKeterangan = !!selectedStatus && selectedStatus !== 'HADIR';
                          const lampiranCount = Array.isArray(supervisiLampiranMap?.[row.key]?.attachments)
                            ? supervisiLampiranMap[row.key].attachments.length
                            : 0;
                          const hasKeteranganText = Boolean(String(supervisiLampiranMap?.[row.key]?.keterangan || '').trim());
                          return (
                            <tr key={row.key}>
                              <td>{row.nama}</td>
                              <td>{row.jamAbsen}</td>
                              <td>{row.lokasi}</td>
                              <td>
                                <select
                                  className="form-select form-select-sm btn btn-outline-primary"
                                  value={selectedStatus}
                                  onChange={(e) => handleSupervisiStatusChange(row.key, e.target.value)}
                                  disabled={supervisiSubmitting}
                                  style={{
                                    borderColor: '#0d6efd',
                                    color: selectedStatus ? '#0d6efd' : '#64748b',
                                    backgroundColor: '#ffffff',
                                  }}
                                >
                                  <option value="">{row.statusAwal === 'BELUM ABSEN' ? 'BELUM ABSEN' : '-'}</option>
                                  {SUPERVISI_STATUS_OPTIONS.map((status) => (
                                    <option key={`${row.key}-${status}`} value={status}>{status}</option>
                                  ))}
                                </select>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {canOpenKeterangan ? (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm w-100 text-center"
                                    style={{ paddingTop: '0.4rem', paddingBottom: '0.4rem' }}
                                    onClick={() => openSupervisiKeteranganModal(row)}
                                    disabled={supervisiSubmitting}
                                  >
                                    {`Keterangan${lampiranCount > 0 || hasKeteranganText ? ` ${lampiranCount > 0 ? `+${lampiranCount}` : ''}` : ''}`}
                                  </button>
                                ) : (
                                  '-'
                                )}
                              </td>
                              <td className="text-center">
                                {row.foto && row.foto !== '-' ? (() => {
                                  const fotoUrl = buildDaftarHadirFotoUrl(row.foto);
                                  const initial = String(row.nama || '?').trim().charAt(0).toUpperCase() || '?';
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => openDaftarHadirFotoModal(row)}
                                      title={`Lihat foto absensi ${row.nama}`}
                                      aria-label={`Lihat foto absensi ${row.nama}`}
                                      style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: '999px',
                                        overflow: 'hidden',
                                        border: `1px solid ${palette.border}`,
                                        padding: 0,
                                        background: '#E2E8F0',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                      }}
                                    >
                                      <img
                                        src={fotoUrl}
                                        alt={`Foto absensi ${row.nama}`}
                                        loading="lazy"
                                        decoding="async"
                                        referrerPolicy="no-referrer"
                                        style={{
                                          width: '100%',
                                          height: '100%',
                                          objectFit: 'cover',
                                          display: 'block',
                                        }}
                                        onError={(e) => {
                                          e.currentTarget.style.display = 'none';
                                          const fallback = e.currentTarget.nextSibling;
                                          if (fallback) fallback.style.display = 'flex';
                                        }}
                                      />
                                      <span
                                        aria-hidden="true"
                                        style={{
                                          display: 'none',
                                          width: '100%',
                                          height: '100%',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          color: palette.brand,
                                          fontSize: 12,
                                          fontWeight: 700,
                                        }}
                                      >
                                        {initial}
                                      </span>
                                    </button>
                                  );
                                })() : '-'}
                              </td>
                              <td className="text-center">
                                <input
                                  type="checkbox"
                                  checked={Boolean(supervisiCheckedMap?.[row.key])}
                                  disabled={!canCheckRow || supervisiSubmitting}
                                  onChange={(e) => toggleSupervisiChecked(row.key, e.target.checked)}
                                />
                              </td>
                            </tr>
                          );
                        })}
                        {!supervisiLoading && supervisiRows.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center text-muted">Tidak ada data.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="d-flex justify-content-end mt-3">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSubmitSupervisi}
                      disabled={supervisiSelectedCount <= 0 || supervisiSubmitting}
                    >
                      {supervisiSubmitting
                        ? 'Menyimpan...'
                        : `Telah Disupervisi oleh ${String(actorNama || actorUsername || '-').trim() || '-'}`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showSupervisiKeteranganModal && (
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1065 }}
            onClick={closeSupervisiKeteranganModal}
          />
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ zIndex: 1070 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) closeSupervisiKeteranganModal();
            }}
          >
            <div className="modal-dialog modal-dialog-scrollable" role="document" style={{ width: '62vw', maxWidth: '62vw' }}>
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <div>
                    <h5 className="modal-title mb-0">{`KETERANGAN - ${String(selectedSupervisiRow?.nama || '-').trim() || '-'}`}</h5>
                    <div className="small text-muted mt-1">{`Tanggal ${formatTanggalPanjang(supervisiTanggal)}`}</div>
                  </div>
                  <button type="button" className="btn-close mt-0" onClick={closeSupervisiKeteranganModal} aria-label="Close" />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label mb-1">Keterangan</label>
                    <textarea
                      rows={2}
                      className="form-control form-control-sm"
                      style={{ backgroundColor: '#E0F2FE', borderColor: '#7DD3FC' }}
                      value={supervisiKeteranganDraft}
                      onChange={(e) => setSupervisiKeteranganDraft(e.target.value)}
                      placeholder="Isi keterangan..."
                    />
                  </div>
                  <div className="px-3 py-2 rounded border bg-white">
                    <div className="small fw-semibold text-secondary mb-2">Upload Dokumen</div>
                    {supervisiInvalidCaptionIndexes.length > 0 ? (
                      <div className="alert alert-warning py-2 mb-2">
                        Caption dokumen wajib diisi untuk semua lampiran.
                      </div>
                    ) : null}
                    <LampiranUpload
                      attachments={supervisiLampiranDraft}
                      setAttachments={setSupervisiLampiranDraft}
                      bisaInput={!supervisiSubmitting}
                      bisaHapus={false}
                      uploadInputClassName="dashboard-supervisi-upload-input"
                      invalidCaptionIndexes={supervisiInvalidCaptionIndexes}
                      onCaptionChange={() => {
                        if (supervisiInvalidCaptionIndexes.length > 0) {
                          setSupervisiInvalidCaptionIndexes([]);
                        }
                      }}
                    />
                  </div>
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={closeSupervisiKeteranganModal} disabled={supervisiSubmitting}>
                      Batal
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={saveSupervisiKeteranganDraft} disabled={supervisiSubmitting}>
                      Lanjutkan
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
